/**
 * 63.3 step 3 (ARCHITECTURE §5.13 "63.3 and 63.4, the design"; lane F): what a shot or a line of sight meets, as
 * shapes with ids, tested exactly along the segment.
 *
 * - **The shapes.** One list per planet, derived from the walk obstacles (core/surfaceWalk.ts — the same props and
 *   structures, the same sizes) and filed on §5.3's cells: a rock or a trunk is an upright cylinder on the ground at
 *   its foot (a profiled rock, 67.13, a stack of cylinders by its tenths; a normal tree, E5.2, its drawn wood — frustums
 *   along a leaning trunk and its limbs, a felled one's stump and lying trunk); a structure is its measured shape from the
 *   body table (constants/hitboxes.ts: boxes in the body's frame, × the person or the planet's prop scale; a ruin's
 *   cut to its rubble's height). Each shape carries what it is (`ObstacleRef`). Memoised on the obstacle list, which
 *   is itself memoised on the state's props, so it is rebuilt only when they change.
 * - **The tests.** A segment against a cylinder or a box, exactly: the first point along it inside (0…1), or none.
 *   Before: a shot tested one point at the end of each substep, and a line of sight one point every quarter unit,
 *   against the cylinders — a sample could step past a thin trunk, and a building was a cylinder 1.75× its height.
 * - **Candidates** come from the cell index along the segment (a cap round points a cell apart, widened by the widest
 *   shape over the nearest the segment comes to the centre less the tallest shape), so a test touches only the shapes
 *   it could meet. `hitWork` counts queries, candidates and primitive tests: the budget tests lock the work, not time.
 *
 * Pure and deterministic: arithmetic and `Math.sqrt` only (exact in every engine); the cell index's scratch stamps
 * are reset per query.
 */
import { footingOf } from './stands';
import type { GameState, PlanetState, StructureState, Vec3 } from '../types';
import { vecAdd, vecScale, vecSub, vecDot, vecLength, vecNormalize, uprightFrame } from '../math';
import { getSurfaceRadius } from './surface';
import { foliageFromNothing, foliageBuildScope } from './foliage';
import { layoutFor } from './modules';
import { MODULE_CELL } from '../constants/modules';
import { planetObstacles, obstacleVariant, obstaclesFromNothing, obstaclesBuildScope, obstacleSplice, type ObstacleRef, type PlanetObstacles, type WalkObstacle, type WalkBox } from './surfaceWalk';
import { withoutTailPatch } from './floraSplice';
import { buildCellIndex, queryCellIndexCount, cellQueryIds, cellsOfItems, fileCellIndex, kthOfSort, allFinite, cellOf, type CellIndex } from './cells';
import { dacos, dcos, dsin, datan2 } from './dmath';
import { TANK } from '../constants/action';
import { personSetOf, PERSON_BODY_HEIGHT, type PersonSet } from './scaleProfile';
import { personSurfaceRadius, type GroundDials } from './fineTerrain';
import { seatRadius, fineEditsOf, editsChanged, type FineEdits } from './propSeat';
import { structureHitbox, structureRuinHeight, ruinHitbox, buildingsByFunction, isPlanetBuilding } from './buildingScale';
import { surfacePropScale } from './propScale';
import { drives } from './body';
import { markedParts, hitPartGone } from './parts';
import { BODIES, type BodyKey } from '../constants/body';
import { TURRET_PARTS_FROM } from '../constants/hitboxes';
import { vehicleFrame } from './vehicleFrame';
import { VEHICLE_STEP_OVER } from '../constants/action';
import type { HitPart } from '../types/body';
import { simWork } from './workCounters';
import { shipBlocks } from './shipDamage';   // (69.3b, the ship as a body: a cycle by design — the ships' shapes are posed from this module's prims; nothing read with the switch off)

/**
 * An upright frustum (`up` a unit vector) from `h0` to `h1` above `base`, its radius `r0` at h0 and `r1` at h1 (a
 * cylinder when they are equal), or a box (`ax`, `ay`, `az` orthonormal, half extents).
 */
export type Prim =
  | { cyl: true; base: Vec3; up: Vec3; r0: number; r1: number; h0: number; h1: number }
  | { cyl: false; c: Vec3; ax: Vec3; ay: Vec3; az: Vec3; hx: number; hy: number; hz: number; /** 63.14 step 3: rays pass it (wire); the walk is held */ pass?: true };

export interface HitShape {
  ref: ObstacleRef; dir: Vec3; prims: Prim[]; /** how far it reaches from its axis (world) */ reach: number;
  /** E5.2: an upright cylinder round all of a many-part shape (a tree's wood, a profiled rock's stack): tested first, so a miss costs one test, not one a part */
  bound?: Prim;
  /** 63.14 step 4: a many-part shape's prims filed by cell of its own frame (a modular building's modules), so a segment tests the prims of the cells it passes, not every part */
  sub?: ShapeSub;
  /** 63.14 step 3: a modular building's shape — every prim a module's part (a wall, a jamb, a sill, a crate), so its walk outline is theirs (outlineDiscs) */
  modular?: true;
}
/**
 * 63.14 step 4 (the measured lever of step 3: a bullet's segment across a 211-prim building tried every prim once its bound
 * was entered): the shape's box prims filed on a grid in the shape's own frame — `o` the grid's corner on the ground,
 * `ax`/`az` its axes, `cell` a cell's side (world), `nx`/`nz` its cells; CSR lists (`start`, `items`) of prim indices per
 * cell, each prim filed in every cell its footprint's corners span. `stamp`/`round`: a visit mark per prim so a query
 * through several cells tests each prim once. Built once per shape (shapeFor), read by firstHit, segmentBlocked and
 * pointBlocked (subPrims / subPrimsAt).
 */
export interface ShapeSub { o: Vec3; ax: Vec3; az: Vec3; cell: number; nx: number; nz: number; start: Int32Array; items: Int32Array; stamp: Uint32Array; round: number }
/** The cells a sub-index's grid has a side (world units): a module's cell at the person scale (2 people), near enough for any layout. */
const SUB_CELL_PEOPLE = 2;
/** File a shape's box prims by cell of its own frame (the first box's axes); null for fewer than SUB_MIN prims. */
const SUB_MIN = 12;
function buildSub(prims: readonly Prim[], base: Vec3, person: number): ShapeSub | null {
  if (prims.length < SUB_MIN) return null;
  const first = prims.find((p): p is Extract<Prim, { cyl: false }> => !p.cyl);
  if (!first) return null;
  const ax = first.ax, az = first.az, cell = SUB_CELL_PEOPLE * person;
  // each prim's footprint across the frame (a box's four corners; a cylinder's disc)
  let minX = Infinity, maxX = -Infinity, minZ = Infinity, maxZ = -Infinity;
  const spans: Array<[number, number, number, number]> = prims.map(p => {
    if (p.cyl) {
      const r = vecSub(p.base, base), x = vecDot(r, ax), z = vecDot(r, az), rr = Math.max(p.r0, p.r1);
      return [x - rr, x + rr, z - rr, z + rr];
    }
    const r = vecSub(p.c, base), x = vecDot(r, ax), z = vecDot(r, az);
    const ex = Math.abs(vecDot(p.ax, ax)) * p.hx + Math.abs(vecDot(p.az, ax)) * p.hz + Math.abs(vecDot(p.ay, ax)) * p.hy;
    const ez = Math.abs(vecDot(p.ax, az)) * p.hx + Math.abs(vecDot(p.az, az)) * p.hz + Math.abs(vecDot(p.ay, az)) * p.hy;
    return [x - ex, x + ex, z - ez, z + ez];
  });
  for (const [x0, x1, z0, z1] of spans) { minX = Math.min(minX, x0); maxX = Math.max(maxX, x1); minZ = Math.min(minZ, z0); maxZ = Math.max(maxZ, z1); }
  const nx = Math.max(1, Math.ceil((maxX - minX) / cell)), nz = Math.max(1, Math.ceil((maxZ - minZ) / cell));
  if (nx * nz > 4096) return null;
  const o = vecAdd(base, vecAdd(vecScale(ax, minX), vecScale(az, minZ)));
  const counts = new Int32Array(nx * nz);
  const cellsOf = (sp: [number, number, number, number]) => {
    const i0 = Math.max(0, Math.floor((sp[0] - minX) / cell)), i1 = Math.min(nx - 1, Math.floor((sp[1] - minX) / cell));
    const j0 = Math.max(0, Math.floor((sp[2] - minZ) / cell)), j1 = Math.min(nz - 1, Math.floor((sp[3] - minZ) / cell));
    return [i0, i1, j0, j1];
  };
  for (const sp of spans) { const [i0, i1, j0, j1] = cellsOf(sp); for (let i = i0; i <= i1; i++) for (let j = j0; j <= j1; j++) counts[i * nz + j]++; }
  const start = new Int32Array(nx * nz + 1);
  for (let c = 0; c < nx * nz; c++) start[c + 1] = start[c] + counts[c];
  const items = new Int32Array(start[nx * nz]), fill = new Int32Array(nx * nz);
  spans.forEach((sp, k) => { const [i0, i1, j0, j1] = cellsOf(sp); for (let i = i0; i <= i1; i++) for (let j = j0; j <= j1; j++) { const c = i * nz + j; items[start[c] + fill[c]++] = k; } });
  return { o, ax, az, cell, nx, nz, start, items, stamp: new Uint32Array(prims.length), round: 0 };
}
/** The prim indices a segment a→b can meet in a sub-indexed shape: the cells it crosses in the shape's frame (a 2D walk), each prim once. */
const subOut: number[] = [];
export function subPrims(sh: HitShape, a: Vec3, b: Vec3): readonly number[] {
  const sub = sh.sub!;
  subOut.length = 0;
  sub.round++;
  if (sub.round === 0xffffffff) { sub.stamp.fill(0); sub.round = 1; }
  const ra = vecSub(a, sub.o), rb = vecSub(b, sub.o);
  const ax0 = vecDot(ra, sub.ax) / sub.cell, az0 = vecDot(ra, sub.az) / sub.cell, ax1 = vecDot(rb, sub.ax) / sub.cell, az1 = vecDot(rb, sub.az) / sub.cell;
  // the cells the segment's footprint crosses: a step of at most a cell along it, each cell visited once (its neighbours too, for the prims that straddle)
  const dx = ax1 - ax0, dz = az1 - az0, steps = Math.max(1, Math.ceil(Math.max(Math.abs(dx), Math.abs(dz)) * 1.01));
  let lastC = -1;
  for (let s = 0; s <= steps; s++) {
    const t = s / steps, x = ax0 + dx * t, z = az0 + dz * t;
    const i = Math.floor(x), j = Math.floor(z);
    if (i < -1 || i > sub.nx || j < -1 || j > sub.nz) continue;
    const ci = Math.max(0, Math.min(sub.nx - 1, i)), cj = Math.max(0, Math.min(sub.nz - 1, j));
    const c = ci * sub.nz + cj;
    if (c === lastC) continue;
    lastC = c;
    for (let q = sub.start[c]; q < sub.start[c + 1]; q++) { const k = sub.items[q]; if (sub.stamp[k] !== sub.round) { sub.stamp[k] = sub.round; subOut.push(k); } }
  }
  return subOut;
}
/** The prim indices that can hold a point in a sub-indexed shape: its cell's. */
export function subPrimsAt(sh: HitShape, pt: Vec3): readonly number[] {
  const sub = sh.sub!;
  const r = vecSub(pt, sub.o), i = Math.floor(vecDot(r, sub.ax) / sub.cell), j = Math.floor(vecDot(r, sub.az) / sub.cell);
  if (i < 0 || i >= sub.nx || j < 0 || j >= sub.nz) return NO_PRIMS;
  const c = i * sub.nz + j;
  return Array.prototype.slice.call(sub.items, sub.start[c], sub.start[c + 1]) as number[];
}
const NO_PRIMS: readonly number[] = [];
export interface PlanetShapes {
  list: HitShape[];
  /** the ordinary shapes on the cells (`filed[k]` = the list index of the index's item k) */
  index: CellIndex;
  filed: Int32Array;
  /** the widest an ordinary shape reaches from its own direction's axis, and the tallest one stands (world units) */
  maxReach: number;
  maxTop: number;
  /**
   * The large shapes (over 4× the 90th percentile's reach or height — a giant classic tree left standing by a person's battle):
   * kept off the cells, so they do not widen every query, and found by a bounding sphere each (list index, centre, radius).
   */
  large: Array<{ i: number; c: Vec3; r: number }>;
  /** the tallest of all the shapes (world), for the plane cull's floor */
  maxTopAll: number;
  /** how far apart candidate queries are made along a path (world): twice the widest ordinary shape's reach, so each
   * query's cap is a narrow band round the line — at least a quarter of a cell */
  stepLen: number;
  stamp: Uint32Array;
  round: number;
  /** 63.4 step 7: a structure's shape by its id (list index), filled on first ask */
  byStructure?: Map<number, number>;
}

// ---------------------------------------------------------------------------
// 63.3 step 4: walking and driving on the shapes' footprints
// ---------------------------------------------------------------------------

/**
 * A body that moves along the ground: its half width, its height, and what it steps over (world units). A person steps
 * over what is lower than its step (the scale profile's `stepOver`, 0.32 people); a vehicle over what is lower than
 * VEHICLE_STEP_OVER of its hull (SCALE §6, decision 96).
 */
export interface Mover { radius: number; height: number; stepOver: number }

/** The person as a mover, from the scale profile. */
export function personMover(ps: { unitRadius: number; stepOver: number; k: number }): Mover {
  return { radius: ps.unitRadius, height: PERSON_BODY_HEIGHT * ps.k, stepOver: ps.stepOver };
}
/** A vehicle as a mover: its footprint radius and hull height (world units). */
export function vehicleMover(radius: number, hullHeight: number): Mover {
  return { radius, height: hullHeight, stepOver: VEHICLE_STEP_OVER * hullHeight };
}

/**
 * The widest a shape reaches from its own axis across the band a mover's body spans — the ground to the mover's height —
 * counting only the parts that rise above what the mover steps over. Null when the whole shape is stepped over.
 * A tree's footprint is its foot (E5.2's rule: walking reads the trunk's foot, the shots its wood), so its pieces are
 * not read here; a structure's comes from its measured parts, a profiled rock's from the tenths the body meets.
 */
function footprintRadius(sh: HitShape, o: WalkObstacle, base: Vec3, up: Vec3, mover: Mover): number | null {
  if (o.pieces) return o.height >= mover.stepOver ? o.radius : null;
  let reach: number | null = null;
  for (const p of sh.prims) {
    if (p.cyl) {
      if (p.h1 < mover.stepOver || p.h0 >= mover.height) continue;   // stepped over, or wholly above the body
      const r = horizontal(vecSub(p.base, base), up) + Math.max(p.r0, p.r1);
      reach = reach === null ? r : Math.max(reach, r);
    } else {
      const centreUp = vecDot(vecSub(p.c, base), up);
      if (centreUp + p.hy < mover.stepOver || centreUp - p.hy >= mover.height) continue;
      // a box's footprint as the disc of its wider side (the walk understands discs): its corners stick out of the disc by
      // at most 41 %, its diagonal would have kept walkers off a crate's whole surround — today's crates and guns read as
      // their half width, and that is what a body meets along the box's axes
      const r = horizontal(vecSub(p.c, base), up) + Math.max(p.hx, p.hz);
      reach = reach === null ? r : Math.max(reach, r);
    }
  }
  return reach;
}

/**
 * 63.9 (the owner: "make buildings and pads functional as cover and prevention from walking or firing through them"):
 * a building's box is wider than a disc can stand for — the one disc of its wider side left a 10-person plinth's corners
 * 1.6 people open to walk into, and the cover map's points on a circle round it (some inside the corners). A box at least
 * `minHalfPeople` across its narrower half walls its outline instead: discs of `discPeople`, their centres that far
 * inside each side and that far apart, so the wall is closed (a body cannot pass between two) and stands within an eighth
 * of a disc of the side; the corners close the same way. The cover map's points then stand along the walls and at the
 * corners. A smaller box, or a cylinder, of the same shape keeps its disc. Only where buildings are sized by function
 * (the classic scale's prop-sized rows are unchanged). Null: no box qualifies. 67.62: a narrower box that is a long thin wall
 * (`wallPeople` long, `wallRatio` its width) walls itself with a row of discs as wide as it.
 */
export const BOX_OUTLINE = { minHalfPeople: 1.5, discPeople: 1, /** 67.62: a narrower box this long (half, people) and this many times its width walls itself as a row (outlineDiscs); 209's footing follow-up: 1.5 — a container 2.2 × 0.8 (ratio 2.7) filed one disc 2.2 wide, a body held 1.4 people off its sides */ wallPeople: 0.8, wallRatio: 1.5 };
/**
 * 63.9 (the coordinator's (a): drops land beside a building, never in it): `dir` moved out of every building's walled box
 * (`BOX_OUTLINE`'s, where buildings are sized by function) by `clearance` (world units) more — across the box's nearest
 * side, keeping its place along it; elsewhere `dir` as it is. Two passes, so a spot pushed into a neighbour comes out too.
 */
/** The planet's walled boxes (`BOX_OUTLINE`'s), kept per shape list; none where buildings are not sized by function. */
const walledMemo = new WeakMap<PlanetShapes, Array<Extract<Prim, { cyl: false }>>>();
const keepOutMemo = new WeakMap<PlanetShapes, Array<Extract<Prim, { cyl: false }>>>();
/**
 * The walled boxes; `keepOut`: and — 63.14 step 3 — each modular building's whole footprint (its layout's cells, a person
 * tall), so drops and spawns land beside a building whose walls are modules (none of which is a walled box), while the
 * cover map's points may stand inside its rooms (the walls' both faces).
 */
function walledBoxes(state: Pick<GameState, 'rocks' | 'flora' | 'structures' | 'planets'> & Partial<Pick<GameState, 'config' | 'ruins'>>, planetIndex: number, keepOut = false): Array<Extract<Prim, { cyl: false }>> {
  if (!state.config || !buildingsByFunction(state.config)) return [];
  const shapes = planetShapes(state, planetIndex);
  const memo = keepOut ? keepOutMemo : walledMemo;
  let boxes = memo.get(shapes);
  if (!boxes) {
    const person = PERSON_BODY_HEIGHT * personSetOf({ config: state.config }).k;
    boxes = shapes.list.flatMap(sh => (sh.ref.kind === 'structure' || sh.ref.kind === 'ruin')
      ? sh.prims.filter((p): p is Extract<Prim, { cyl: false }> => !p.cyl && Math.min(p.hx, p.hz) >= BOX_OUTLINE.minHalfPeople * person) : []);
    if (keepOut) {
      const planet = state.planets[planetIndex];
      for (const s of state.structures) {
        if (s.planetIndex !== planetIndex || s.health <= 0) continue;
        const layout = layoutFor(s, state.config);
        if (!layout) continue;
        const up = vecNormalize(s.surfaceDir), { tx, tz } = structureFrame(s, up);
        const base = vecAdd(planet.position, vecScale(up, getSurfaceRadius(planet, up) + (s.baseLift ?? 0)));
        boxes.push({ cyl: false, c: vecAdd(base, vecScale(up, person)), ax: tx, ay: up, az: tz, hx: (layout.cols * MODULE_CELL * person) / 2, hy: person, hz: (layout.rows * MODULE_CELL * person) / 2 });
      }
    }
    memo.set(shapes, boxes);
  }
  return boxes;
}
/** 63.9: does `dir`'s ground stand inside a building's walled box (by `margin` more, world units)? `keepOut`: or a modular building's footprint (drops, spawns). */
export function insideBuilding(state: Pick<GameState, 'rocks' | 'flora' | 'structures' | 'planets'> & Partial<Pick<GameState, 'config' | 'ruins'>>, planetIndex: number, dir: Vec3, margin = 0, keepOut = false): boolean {
  const boxes = walledBoxes(state, planetIndex, keepOut);
  if (!boxes.length) return false;
  const planet = state.planets[planetIndex], at = vecAdd(planet.position, vecScale(vecNormalize(dir), planet.radius));
  return boxes.some(p => { const rel = vecSub(at, p.c); return Math.abs(vecDot(rel, p.ax)) < p.hx + margin && Math.abs(vecDot(rel, p.az)) < p.hz + margin; });
}
export function outOfBuildings(state: Pick<GameState, 'rocks' | 'flora' | 'structures' | 'planets'> & Partial<Pick<GameState, 'config' | 'ruins'>>, planetIndex: number, dir: Vec3, clearance: number): Vec3 {
  const planet = state.planets[planetIndex];
  if (!planet || !state.config || !buildingsByFunction(state.config)) return dir;
  const boxes = walledBoxes(state, planetIndex, true);   // (63.14 step 3: and the modular buildings' footprints)
  let d = vecNormalize(dir);
  for (let pass = 0; pass < 2; pass++) {
    let moved = false;
    for (const p of boxes) {
      const at = vecAdd(planet.position, vecScale(d, planet.radius)), rel = vecSub(at, p.c);
      const x = vecDot(rel, p.ax), z = vecDot(rel, p.az), ex = p.hx + clearance - Math.abs(x), ez = p.hz + clearance - Math.abs(z);
      if (ex <= 0 || ez <= 0) continue;
      const nx = ex < ez ? (x < 0 ? -1 : 1) * (p.hx + clearance) : x, nz = ex < ez ? z : (z < 0 ? -1 : 1) * (p.hz + clearance);
      d = vecNormalize(vecSub(vecAdd(at, vecAdd(vecScale(p.ax, nx - x), vecScale(p.az, nz - z))), planet.position));
      moved = true;
    }
    if (!moved) break;
  }
  return d;
}
function outlineDiscs(planet: PlanetState, sh: HitShape, o: WalkObstacle, base: Vec3, up: Vec3, mover: Mover, person: number): WalkObstacle[] | null {
  if (o.ref?.kind !== 'structure' && o.ref?.kind !== 'ruin') return null;
  const rho = BOX_OUTLINE.discPeople * person;
  const out: WalkObstacle[] = [];
  let walled = false;
  const disc = (at: Vec3, radius: number, box?: WalkBox) => out.push({ ...o, dir: vecNormalize(vecSub(at, planet.position)), radius, ...(box ? { box } : {}) });
  for (const p of sh.prims) {
    if (p.cyl) {
      if (p.h1 < mover.stepOver || p.h0 >= mover.height) continue;
      disc(p.base, Math.max(p.r0, p.r1));
      continue;
    }
    const centreUp = vecDot(vecSub(p.c, base), up);
    if (centreUp + p.hy < mover.stepOver || centreUp - p.hy >= mover.height) continue;
    const ground = vecSub(p.c, vecScale(up, centreUp));   // the box's centre down at the shape's base
    const lo = Math.min(p.hx, p.hz), hi = Math.max(p.hx, p.hz);
    // 63.16 (i): the rectangle every disc of this box stands for — the walk meets it exactly (core/surfaceWalk.ts obstacleGap)
    const box: WalkBox = { c: vecNormalize(vecSub(ground, planet.position)), ax: p.ax, az: p.az, hx: p.hx, hz: p.hz };
    // (63.14: a modular building's every box is a module's part — a wall panel, a window's sill or jambs, a crate — so each
    // walls itself: a thin one as a row whatever its length (a sill is 1.2 people, under wallPeople), any as a disc, and the
    // shape counts as walled. Without this a building of window walls alone — the greenhouse — filed no wall and fell back
    // to one disc of its whole footprint, 6.5 people across, that held a body 2.5 people off it and let none in.)
    const thin = lo < BOX_OUTLINE.minHalfPeople * person && hi >= BOX_OUTLINE.wallRatio * lo && (sh.modular || hi >= BOX_OUTLINE.wallPeople * person);
    if (thin) {
      // 67.62: a long thin wall (the D HQ's and FOB's Hesco) — a row of discs as wide as the wall (a quarter person at least), a
      // radius apart so it is closed; one disc of its length would close the yard it walls and reach as far past it
      const r = Math.max(lo, 0.25 * person), along = p.hx >= p.hz ? p.ax : p.az, n = Math.max(1, Math.ceil((2 * (hi - r)) / r));
      for (let k = 0; k <= n; k++) disc(vecAdd(ground, vecScale(along, -(hi - r) + (k * 2 * (hi - r)) / n)), r, box);
      walled = true;   // (a shape of walls alone is walled too: else it falls back to one footprint disc — the FOB's U shut; lane F's catch)
      continue;
    }
    if (lo < BOX_OUTLINE.minHalfPeople * person) { disc(ground, hi, box); if (sh.modular) walled = true; continue; }
    walled = true;
    const at = (x: number, z: number) => vecAdd(ground, vecAdd(vecScale(p.ax, x), vecScale(p.az, z)));
    const ix = p.hx - rho, iz = p.hz - rho;
    const nx = Math.max(1, Math.ceil((2 * ix) / rho)), nz = Math.max(1, Math.ceil((2 * iz) / rho));
    for (let i = 0; i <= nx; i++) { const x = -ix + (2 * ix * i) / nx; disc(at(x, -iz), rho, box); disc(at(x, iz), rho, box); }
    for (let i = 1; i < nz; i++) { const z = -iz + (2 * iz * i) / nz; disc(at(-ix, z), rho, box); disc(at(ix, z), rho, box); }
  }
  // (209's footing follow-up, lane F, tried and withdrawn: a shape no box of which walls itself filing its parts' discs instead of
  // one of its whole reach — the antenna's post's bound holds a body 0.4 people off its sides in thin air — cost the budget scene
  // (25 a side) 45 % more prim tests and 32 % more person-ground lookups a tick (tests/frameBudget.test.ts), the same with
  // vehicles and cylinders left out; the post keeps its one disc.)
  return walled ? out : null;
}

/** 67.52: a structure obstacle's base lift (StructureState.baseLift; 0 for anything else or none). */
/**
 * 71.2: the ground an obstacle stands on — a rock, a tree, a piece of debris on the fine ground by its seat (core/propSeat.ts,
 * as the walk, its crown and its drawing take it); a structure or a ruin on the mesh plus its build's lift (67.52; 71.5's).
 */
function obstacleGround(state: Partial<Pick<GameState, 'structures' | 'config'>>, planet: PlanetState, o: WalkObstacle, up: Vec3): number {
  return o.seat ? seatRadius(planet, o.seat, up, state.config ? personSetOf({ config: state.config }) : null) : getSurfaceRadius(planet, up) + liftOf(state, o);
}

function liftOf(state: Partial<Pick<GameState, 'structures'>>, o: WalkObstacle): number {
  if (o.ref?.kind !== 'structure' || !state.structures) return 0;
  const id = o.ref.id, at = state.structures[id];
  return (at && at.id === id ? at : state.structures.find(x => x.id === id))?.baseLift ?? 0;
}

/**
 * 63.3 step 4 (lane F's follow-up): a lying piece's footprint is a capsule along the ground, which the walk takes as a
 * row of discs of the piece's radius, spaced so a mover cannot slip between two (centres at most the disc's radius plus
 * the mover's apart; a body passes between two discs only when they stand more than twice that apart). Each disc stands
 * at the piece's top there; a piece the mover steps over, or one wholly above its body, files none.
 */
function lyingDiscs(state: Partial<Pick<GameState, 'structures' | 'config'>>, planet: PlanetState, o: WalkObstacle, mover: Mover): WalkObstacle[] {
  const out: WalkObstacle[] = [];
  const up = vecNormalize(o.dir);
  const foot = vecAdd(planet.position, vecScale(up, obstacleGround(state, planet, o, up)));   // (71.2: where its shape stands)
  const pieces = o.pieces!;
  for (let i = o.lyingFrom!; i < pieces.length; i++) {
    const p = pieces[i];
    const ha = vecDot(p.a, up), hb = vecDot(p.b, up);
    const top = Math.max(ha + p.ra, hb + p.rb), bottom = Math.min(ha - p.ra, hb - p.rb);
    if (top < mover.stepOver || bottom >= mover.height) continue;
    const r = Math.max(p.ra, p.rb);
    const len = horizontal(vecSub(p.b, p.a), up);
    const n = Math.max(1, Math.ceil(len / (r + mover.radius)));
    for (let j = 0; j <= n; j++) {
      const t = j / n;
      const at = vecAdd(foot, vecAdd(p.a, vecScale(vecSub(p.b, p.a), t)));
      out.push({ dir: vecNormalize(vecSub(at, planet.position)), radius: p.ra + (p.rb - p.ra) * t, height: Math.max(0, (ha + p.ra) + ((hb + p.rb) - (ha + p.ra)) * t), ref: o.ref });
    }
  }
  return out;
}

let footprintMemo = new WeakMap<PlanetShapes, Map<string, PlanetObstacles>>();
/** 63.4 partial destruction step 1: an obstacle's footprints for a mover, kept while its shape and the ground stay the same objects — 69.67: per mover (a soldier's, a bike's, a tank's, a truck's: kept for one, the next mover's build remade every obstacle's, ~11,000 on the owner's board in one tick). */
let footprintKept = new WeakMap<WalkObstacle, Map<string, { sh: HitShape | undefined; heightmap: Float32Array; out: WalkObstacle[] }>>();
/** 63.4 pd step 1: each planet's and mover's last footprints, for their index. */
let lastFootprints = new Map<string, PlanetObstacles>();
/** E5.4's tail patch: each planet's and mover's last footprints with the obstacles and shapes they were made over, each obstacle's run of discs, and the discs' cells (when filed afresh). */
let footprintRecs = new Map<string, { value: PlanetObstacles; po: PlanetObstacles; shapes: PlanetShapes; runs: Int32Array; offsets: Int32Array; cells: Uint32Array | undefined; n: number; /** E5.4, craters in the wood: the ground and the shapes' filing they were made on */ heightmap?: Float32Array; shapeAt?: Int32Array }>();
/** E5.4's tail patch: a patched shape list → the shape list it was patched from (its kept shapes are those objects). */
const shapesPatchedFrom = new WeakMap<PlanetShapes, PlanetShapes>();
const sameDirection = (a: Vec3, b: Vec3) => a === b || (a.x === b.x && a.y === b.y && a.z === b.z);

/** Each shape list's source obstacles, parallel to its list (each ref'd obstacle has one shape). */
const shapeOfObstacle = new WeakMap<PlanetShapes, WalkObstacle[]>();
/** A planet's shapes by their obstacle (the object), made once per shape list. */
let byRefMemo = new WeakMap<PlanetShapes, Map<WalkObstacle, HitShape>>();
function shapesByObstacle(shapes: PlanetShapes): Map<WalkObstacle, HitShape> {
  let m = byRefMemo.get(shapes);
  if (!m) { const src = shapeOfObstacle.get(shapes) ?? []; m = new Map(); for (let i = 0; i < src.length; i++) m.set(src[i], shapes.list[i]); byRefMemo.set(shapes, m); }
  return m;
}

/**
 * 63.3 step 4: what a mover walks or drives against on a planet — each shape it cannot step over, as a disc round the
 * shape's direction the width the body meets (`footprintRadius`), filed by cell exactly as `planetObstacles` is, so
 * `stepWalk` and the steer take the list unchanged. Walking and driving read the same shapes; what differs is the
 * mover. Memoised per shapes and mover (the shapes are memoised on the world, so a tick costs a lookup).
 */
export function footprintObstacles(state: Pick<GameState, 'rocks' | 'flora' | 'structures' | 'planets'> & Partial<Pick<GameState, 'config'>>, planetIndex: number, mover: Mover): PlanetObstacles {
  const po = planetObstacles(state, planetIndex);
  const planet = state.planets[planetIndex];
  if (!planet || po.list.length === 0) return po;
  const shapes = planetShapes(state, planetIndex);
  const key = `${mover.radius}|${mover.height}|${mover.stepOver}`;
  let perShapes = footprintMemo.get(shapes);
  if (!perShapes) { perShapes = new Map(); footprintMemo.set(shapes, perShapes); }
  const hit = perShapes.get(key);
  if (hit) return hit;
  const sk = planetIndex * 2 + obstacleVariant(state), lastKey = `${sk}|${key}`;   // (69.67: the world with its static structures and without, apart)
  // E5.4's tail patch: the obstacles and the shapes were both patched for forest cells joining and leaving, from those
  // these footprints were built over — each kept obstacle's discs as they were (its shape the same object, on the same
  // ground: what a full build keeps), the joined ones' made, the index filed from the kept cells
  const spl = obstacleSplice(po), rec = footprintRecs.get(lastKey), ls = lastShapes.get(sk);
  if (spl && rec && rec.po === spl.prev && shapesPatchedFrom.get(shapes) === rec.shapes && ls && ls.shapes === shapes) {
    const runs = new Int32Array(po.list.length), list: WalkObstacle[] = [], cellsKept: number[] = [];
    const n = po.index.n, keepCells = !!rec.cells && rec.n === n;
    let oi = 0, maxRadius = 0, maxHeight = 0;
    for (let ni = 0; ni < po.list.length; ni++) {
      let mine: readonly WalkObstacle[];
      if (ni >= spl.at && ni < spl.at + spl.count) {
        const o = po.list[ni], si = ls.shapeAt[ni];
        mine = discsOf(o, o.ref && si >= 0 ? shapes.list[si] : undefined, key, planet, state, mover);
        for (const d of mine) cellsKept.push(keepCells ? cellOf(d.dir.x, d.dir.y, d.dir.z, n) : 0);
      } else {
        while (spl.gone[oi]) oi++;
        if (po.list[ni] !== rec.po.list[oi]) {
          // (an obstacle re-made in its slot beside the join — a structure replaced in place: its footprints made again)
          const o = po.list[ni], si = ls.shapeAt[ni];
          mine = discsOf(o, o.ref && si >= 0 ? shapes.list[si] : undefined, key, planet, state, mover);
          for (const d of mine) cellsKept.push(keepCells ? cellOf(d.dir.x, d.dir.y, d.dir.z, n) : 0);
          oi++;
        } else {
          const from = rec.offsets[oi], len = rec.runs[oi], old = rec.value.list;
          for (let k = 0; k < len; k++) { const d = old[from + k]; list.push(d); if (d.radius > maxRadius) maxRadius = d.radius; if (d.height > maxHeight) maxHeight = d.height; }
          if (keepCells) for (let k = 0; k < len; k++) cellsKept.push(rec.cells![from + k]);
          runs[ni] = len;
          oi++;
          continue;
        }
      }
      runs[ni] = mine.length;
      for (const d of mine) { list.push(d); if (d.radius > maxRadius) maxRadius = d.radius; if (d.height > maxHeight) maxHeight = d.height; }
    }
    const same = list.length === po.list.length && list.every((x, i) => x === po.list[i]);
    const cells = keepCells ? Uint32Array.from(cellsKept) : cellsOfItems(list.length, i => list[i].dir, n);
    const value: PlanetObstacles = same ? po : { list, index: fileCellIndex(cells, n), maxRadius, maxHeight };
    if (!same) lastFootprints.set(lastKey, value);
    perShapes.set(key, value);
    footprintRecs.set(lastKey, { value, po, shapes, runs, offsets: offsetsOf(runs), cells, n, heightmap: planet.heightmap, shapeAt: ls.shapeAt });
    simWork.tableJoinPatches++;
    return value;
  }
  // E5.4, craters and fellings in the wood: the obstacles and the shapes patched in place over the records these footprints
  // were made from (the same filing) — each unchanged obstacle's run of discs as it was (its obstacle and its shape the same
  // objects, its ground the same for a lying trunk: what a full build keeps), the changed ones made again, the index kept
  // where the directions are (the full build's refile) else filed from the kept cells
  if (rec && rec.po.index === po.index && rec.po.list.length === po.list.length && rec.shapeAt && ls && ls.shapes === shapes && ls.shapeAt === rec.shapeAt && rec.heightmap) {
    const runs = new Int32Array(po.list.length), list: WalkObstacle[] = [], n = po.index.n, keepCells = !!rec.cells && rec.n === n;
    const cellsKept: number[] = [], moved = planet.heightmap !== rec.heightmap;
    let maxRadius = 0, maxHeight = 0, sameDirs = true, changed = 0;
    for (let i = 0; i < po.list.length; i++) {
      const o = po.list[i], si = ls.shapeAt[i], sh = o.ref && si >= 0 ? shapes.list[si] : undefined;
      const from = rec.offsets[i], len = rec.runs[i], old = rec.value.list;
      if (o === rec.po.list[i] && sh === (o.ref && si >= 0 ? rec.shapes.list[si] : undefined) && !(moved && o.pieces && o.lyingFrom !== undefined)) {
        for (let k = 0; k < len; k++) { const d = old[from + k]; list.push(d); if (d.radius > maxRadius) maxRadius = d.radius; if (d.height > maxHeight) maxHeight = d.height; }
        if (keepCells) for (let k = 0; k < len; k++) cellsKept.push(rec.cells![from + k]);
        runs[i] = len;
        continue;
      }
      changed++;
      const mine = discsOf(o, sh, key, planet, state, mover);
      if (mine.length !== len) sameDirs = false;
      for (let k = 0; k < mine.length; k++) {
        const d = mine[k];
        if (sameDirs && !sameDirection(d.dir, old[from + k].dir)) sameDirs = false;
        list.push(d); if (d.radius > maxRadius) maxRadius = d.radius; if (d.height > maxHeight) maxHeight = d.height;
        if (keepCells) cellsKept.push(cellOf(d.dir.x, d.dir.y, d.dir.z, n));
      }
      runs[i] = mine.length;
    }
    const same = list.length === po.list.length && list.every((x, i) => x === po.list[i]);
    const prevValue = rec.value;
    const cells = keepCells ? Uint32Array.from(cellsKept) : cellsOfItems(list.length, i => list[i].dir, n);
    const value: PlanetObstacles = same ? po : { list, index: sameDirs && prevValue.index.n === n && prevValue.list.length === list.length ? prevValue.index : fileCellIndex(cells, n), maxRadius, maxHeight };
    if (!same) lastFootprints.set(lastKey, value);
    perShapes.set(key, value);
    footprintRecs.set(lastKey, { value, po, shapes, runs, offsets: offsetsOf(runs), cells, n, heightmap: planet.heightmap, shapeAt: ls.shapeAt });
    simWork.tableCraterPatches++;
    void changed;
    return value;
  }
  const byObstacle = shapesByObstacle(shapes);
  const list: WalkObstacle[] = [];
  const runs = new Int32Array(po.list.length);
  let maxRadius = 0, maxHeight = 0;
  for (let oi = 0; oi < po.list.length; oi++) {
    const o = po.list[oi];
    const sh = o.ref ? byObstacle.get(o) : undefined;
    const mine = discsOf(o, sh, key, planet, state, mover);
    runs[oi] = mine.length;
    for (const d of mine) {
      list.push(d);
      if (d.radius > maxRadius) maxRadius = d.radius;
      if (d.height > maxHeight) maxHeight = d.height;   // a footprint keeps its obstacle's height
    }
  }
  const same = list.length === po.list.length && list.every((x, i) => x === po.list[i]);
  // 63.4 pd step 1: the last footprints' index where the same directions are filed again at the same cells per edge (an
  // index is its directions and its cells per edge alone)
  const prev = lastFootprints.get(lastKey);
  const refile = !same && prev && prev.index.n === po.index.n && prev.list.length === list.length && prev.list.every((x, i) => x === list[i] || sameDirection(x.dir, list[i].dir));
  // (the cells kept beside the list always, so a patch after a crater files from them)
  const cells = cellsOfItems(list.length, i => list[i].dir, po.index.n);
  const value: PlanetObstacles = same ? po : { list, index: refile ? prev!.index : fileCellIndex(cells, po.index.n), maxRadius, maxHeight };
  if (!same) lastFootprints.set(lastKey, value);
  perShapes.set(key, value);
  const lsNow = lastShapes.get(sk);
  footprintRecs.set(lastKey, { value, po, shapes, runs, offsets: offsetsOf(runs), cells, n: po.index.n, heightmap: planet.heightmap, ...(lsNow && lsNow.shapes === shapes ? { shapeAt: lsNow.shapeAt } : {}) });
  return value;
}

/**
 * An obstacle's footprints for a mover (63.3 step 4), kept while it, its shape, the ground and the mover are the same
 * (63.4 pd step 1) — the full build's and the tail patch's one way to make them.
 */
function discsOf(o: WalkObstacle, sh: HitShape | undefined, key: string, planet: PlanetState, state: Partial<Pick<GameState, 'config' | 'structures'>>, mover: Mover): WalkObstacle[] {
  // 63.4 partial destruction step 1: an obstacle's footprints are kept while it, its shape, the ground and the mover are
  // the same — a felling recomputes the felled tree's alone, not every obstacle's on the planet
  // (67.45: across a crater too — a shape is kept only where the ground under it did not move, so the same shape is the
  // same ground under its footprint, and an obstacle with none reads no ground; a felled trunk's lying discs read the
  // ground along it, so they keep only on the same ground)
  let byKey = footprintKept.get(o);
  const kept = byKey?.get(key);
  let mine: WalkObstacle[];
  if (kept && kept.sh === sh && (kept.heightmap === planet.heightmap || !(o.pieces && o.lyingFrom !== undefined))) mine = kept.out;
  else {
    mine = [];
    let radius: number | null = null;
    // 209's follow-up: a walkable top where it stands as one (seated at the person scale, core/stands.ts) is walked on — no
    // wall (its deck is ground, core/standGround.ts; its edge a step or a ramp): the pad's hit box's bound walled 6 people
    // round it. Its shape stays (rounds, sight, cover read it).
    // (209's footing follow-up, lane F: a building's plinth is a floor too — footingOf — but its walls stay: the outline's
    // discs are kept, only the one disc of its whole footprint goes, so the pad is walked on and the walls still hold)
    const stood = o.ref?.kind === 'structure' ? state.structures?.find(x => x.id === o.ref!.id) : undefined;
    const floor = !!stood && stood.baseLift !== undefined && !!footingOf(stood, state.config);
    if (!sh) { if (!floor) radius = o.height >= mover.stepOver ? o.radius : null; }   // no shape (a ref-less obstacle): the walk's own disc
    else {
      const up = vecNormalize(o.dir);
      const base = vecAdd(planet.position, vecScale(up, obstacleGround(state, planet, o, up)));   // (67.52: a build's lift; 71.2: a prop on the fine ground)
      // 63.9: a building sized by function (and its ruin) walls its box's outline; anything else, its one disc
      const walls = state.config && buildingsByFunction(state.config) ? outlineDiscs(planet, sh, o, base, up, mover, PERSON_BODY_HEIGHT * personSetOf({ config: state.config }).k) : null;
      if (walls) mine.push(...walls);
      else if (!floor) radius = footprintRadius(sh, o, base, up, mover);
    }
    if (radius !== null) mine.push(radius === o.radius ? o : { ...o, radius });
    // a felled tree's trunk lying along the ground: a row of discs along each lying piece the mover cannot step over
    if (o.pieces && o.lyingFrom !== undefined) for (const d of lyingDiscs(state, planet, o, mover)) mine.push(d);
    if (!byKey) { byKey = new Map(); footprintKept.set(o, byKey); }
    byKey.set(key, { sh, heightmap: planet.heightmap, out: mine });
  }
  return mine;
}

/** Each run's start, from the runs' lengths. */
function offsetsOf(runs: Int32Array): Int32Array {
  const out = new Int32Array(runs.length);
  for (let i = 1; i < runs.length; i++) out[i] = out[i - 1] + runs[i - 1];
  return out;
}

/** Work done, for the budget tests: cell queries, candidate shapes visited, primitive tests made. */
export const hitWork = { queries: 0, candidates: 0, prims: 0 };
export function resetHitWork(): void { hitWork.queries = 0; hitWork.candidates = 0; hitWork.prims = 0; }

let memo = new WeakMap<PlanetObstacles, PlanetShapes>();

/** The planet's shapes (memoised on its walk obstacles). */
export function planetShapes(state: Pick<GameState, 'rocks' | 'flora' | 'structures' | 'planets'> & Partial<Pick<GameState, 'config' | 'ruins'>>, planetIndex: number): PlanetShapes {
  const po = planetObstacles(state, planetIndex);
  let ps = memo.get(po);
  if (!ps) { ps = buildShapes(state, planetIndex, po); memo.set(po, ps); }
  return ps;
}

/** `bottom` (≤ 0): how far its parts reach under the base — a tree's wood is sunk as drawn (63.17: a giant's trunk, its large sphere). */
type ShapeBound = { reach: number; top: number; bottom: number; base: Vec3; up: Vec3 };
/**
 * 62.56: each planet index's last shapes — the obstacle each was built from, its bounds, and the ground it stood on — so
 * the next build keeps a shape whose obstacle is the same object and whose ground did not move. A shape's base is the
 * ground at its direction, which reads the three vertices of the face under it (heights move each vertex along its own
 * radius, so which face is under a direction never changes); a vertex that moved can only be under a direction within a
 * face's longest edge of it.
 */
let lastShapes = new Map<number, { shapes: PlanetShapes; src: WalkObstacle[]; bounds: ShapeBound[]; heightmap: Float32Array; edits: FineEdits; basePositions: Float32Array; radius: number; position: Vec3; config: unknown; poList: WalkObstacle[]; poIndex: PlanetObstacles['index']; shapeAt: Int32Array; mReach?: number; mTop?: number; /** E5.4's tail patch: each shape's cell at the index's cells per edge */ cells?: Uint32Array }>();

/**
 * E5.4's tail patch, for the tests (lane A's read): the four tables as play builds them without the patch — the memos
 * empty, the kept records and entities as they are, no join or leave patched — then every memo and record put back, so
 * the patched path runs from the same records. Its lists are the very objects a patched build keeps.
 */
export function fullBuild<T>(fn: () => T): T {
  const saved = { memo, lastShapes: new Map(lastShapes), footprintMemo, lastFootprints: new Map(lastFootprints), footprintRecs: new Map(footprintRecs) };
  memo = new WeakMap(); footprintMemo = new WeakMap();
  try { return withoutTailPatch(() => obstaclesBuildScope(() => foliageBuildScope(fn))); } finally {
    ({ memo, footprintMemo } = saved); lastShapes = saved.lastShapes; lastFootprints = saved.lastFootprints; footprintRecs = saved.footprintRecs;
  }
}

/** 62.56, for the tests: run `fn` with nothing kept — the obstacles and the shapes built from nothing — then put back what was kept. */
export function fromNothing<T>(fn: () => T): T {
  const saved = { memo, lastShapes, footprintMemo, footprintKept, byRefMemo, lastFootprints, footprintRecs };
  memo = new WeakMap(); lastShapes = new Map(); footprintMemo = new WeakMap(); footprintKept = new WeakMap(); byRefMemo = new WeakMap(); lastFootprints = new Map(); footprintRecs = new Map();
  try { return obstaclesFromNothing(() => foliageFromNothing(fn)); } finally { ({ memo, lastShapes, footprintMemo, footprintKept, byRefMemo, lastFootprints, footprintRecs } = saved); }
}
/** The longest edge of a planet mesh's faces, as an angle (the non-indexed icosphere: a face a vertex triple). */
const edgeAngles = new WeakMap<Float32Array, number>();
function longestEdge(bp: Float32Array): number {
  let a = edgeAngles.get(bp);
  if (a !== undefined) return a;
  let minCos = 1;
  const cosAt = (i: number, j: number) => {
    const ix = bp[i * 3], iy = bp[i * 3 + 1], iz = bp[i * 3 + 2], jx = bp[j * 3], jy = bp[j * 3 + 1], jz = bp[j * 3 + 2];
    return (ix * jx + iy * jy + iz * jz) / (Math.sqrt(ix * ix + iy * iy + iz * iz) * Math.sqrt(jx * jx + jy * jy + jz * jz) || 1);
  };
  for (let t = 0; t + 2 < bp.length / 3; t += 3) minCos = Math.min(minCos, cosAt(t, t + 1), cosAt(t + 1, t + 2), cosAt(t, t + 2));
  a = dacos(Math.max(-1, Math.min(1, minCos)));
  edgeAngles.set(bp, a);
  return a;
}

/**
 * 71.2: `moved` with the obstacles a changed edit reaches added (each within the edit's reach and the widest walk disc,
 * which a seat's footprint never exceeds) — or null (nothing kept) when too many edits changed to be worth it. Without the
 * fine ground no seat reads the edits: `moved` as it is.
 */
function movedByEdits(before: FineEdits, planet: PlanetState, po: PlanetObstacles, config: GameState['config'] | undefined, moved: Set<number>): Set<number> | null {
  const ps = config ? personSetOf({ config }) : null;
  if (!ps || !(ps.fineAmplitude > 0)) return moved;
  const changed = editsChanged(before, planet, ps.k);
  if (changed.length === 0) return moved;
  if (changed.length > 64) return null;
  for (const c of changed) {
    const l = Math.sqrt(c.dir.x * c.dir.x + c.dir.y * c.dir.y + c.dir.z * c.dir.z) || 1;
    const n = queryCellIndexCount(po.index, { x: c.dir.x / l, y: c.dir.y / l, z: c.dir.z / l }, (c.reach + po.maxRadius) / planet.radius, false), ids = cellQueryIds();
    for (let q = 0; q < n; q++) moved.add(ids[q]);
  }
  return moved;
}

/**
 * 62.56: which of `po`'s obstacles stand where the ground moved since `before` — a superset (the obstacles filed within
 * twice a face's longest edge of a moved vertex), or null when too much moved to be worth keeping anything.
 */
function movedUnder(planet: PlanetState, before: Float32Array, po: PlanetObstacles): Set<number> | null {
  const h = planet.heightmap, bp = planet.basePositions;
  const moved = new Set<number>();
  let changed = 0;
  const reach = 2 * longestEdge(bp);
  for (let v = 0; v < h.length; v++) {
    if (h[v] === before[v]) continue;
    if (++changed > h.length / 8) return null;
    const x = bp[v * 3], y = bp[v * 3 + 1], z = bp[v * 3 + 2], l = Math.sqrt(x * x + y * y + z * z) || 1;
    { const c = queryCellIndexCount(po.index, { x: x / l, y: y / l, z: z / l }, reach, false), ids = cellQueryIds(); for (let q = 0; q < c; q++) moved.add(ids[q]); }   // (62.57: the cells' buffer, read at once)
  }
  return moved;
}

/** A large shape's bounding sphere: about the middle of its height, from its sunk bottom to its top, its reach across. */
function largeSphere(i: number, b: ShapeBound): PlanetShapes['large'][number] {
  const half = (b.top - b.bottom) / 2;
  return { i, c: vecAdd(b.base, vecScale(b.up, b.bottom + half)), r: Math.sqrt(b.reach * b.reach + half * half) };
}

function buildShapes(state: Pick<GameState, 'structures' | 'planets'> & Partial<Pick<GameState, 'config' | 'ruins'>>, planetIndex: number, po: PlanetObstacles): PlanetShapes {
  const planet = state.planets[planetIndex];
  const person = PERSON_BODY_HEIGHT * (state.config ? personSetOf({ config: state.config }).k : 1);
  const structures = new Map((state.structures ?? []).map(s => [s.id, s]));
  const ruins = new Map((state.ruins ?? []).map(r => [r.id, r]));
  const list: HitShape[] = [];
  const bounds: ShapeBound[] = [];
  const src: WalkObstacle[] = [];
  // 62.56: the last shapes of this planet, kept where their obstacle is the same object on unmoved ground
  const sk = planetIndex * 2 + obstacleVariant(state);   // (69.67: by variant, as the obstacles' filing)
  const last = lastShapes.get(sk);
  const same = last && last.basePositions === planet.basePositions && last.radius === planet.radius && last.config === state.config
    && last.position.x === planet.position.x && last.position.y === planet.position.y && last.position.z === planet.position.z
    && last.heightmap.length === planet.heightmap.length ? last : null;
  let moved = same ? (same.heightmap === planet.heightmap ? new Set<number>() : movedUnder(planet, same.heightmap, po)) : null;
  // 71.2: a prop stands on the fine ground's edits too (core/propSeat.ts) — a dig, a craterlet or a terrace come or gone
  // moves the seats within its reach, which are made again with the moved ground's
  if (same && moved) moved = movedByEdits(same.edits, planet, po, state.config, moved);
  // (the by-obstacle map of the last shapes only where the full build below reads it: a patch path need not build it — O(n))
  let keep: Map<WalkObstacle, number> | null | undefined;
  const keepOf = () => (keep === undefined ? (keep = same && moved ? new Map(same.src.map((o, i) => [o, i])) : null) : keep);
  // one obstacle's shape and its bound over the ground at its direction, or null (nothing to it)
  const shapeFor = (o: WalkObstacle & { ref: ObstacleRef }): { sh: HitShape; b: ShapeBound } | null => {
      const up = vecNormalize(o.dir);
      const base = vecAdd(planet.position, vecScale(up, obstacleGround(state, planet, o, up)));   // (67.52: a build's lift; 71.2: a prop on the fine ground)
      const prims: Prim[] = [];
      let reach = 0, top = 0, bottom = 0, modular = false;
      const s = o.ref.kind === 'structure' ? structures.get(o.ref.id) : undefined;
      const r = o.ref.kind === 'ruin' ? ruins.get(o.ref.id) : undefined;   // 63.7 (b)'s follow-up: a ruin that is a body
      if (s || r) {
        // 63.7 (b): a planet-scale building sized by function has its own model's shape; a ruin its person-scale model's
        const def = s ? structureHitbox(s, state.config) : ruinHitbox(r!);
        modular = !!def.modular;   // 63.14 step 3: a layout's modules (many parts: bounded below; no marked parts)
        const scale = def.ruler === 'prop' ? surfacePropScale(planet.radius) : def.ruler === 'unit' ? PERSON_BODY_HEIGHT : person;
        const ruin = !s || s.health > 0 ? 1 : structureRuinHeight(s, state.config);
        // the frame the model is drawn in (the renderer's upright seat), so its parts stand where it is drawn — 63.1f's
        // follow-up: a turret's parts turned with it (partFrame), as the drawn turret turns
        for (let k = 0; k < def.parts.length; k++) {
          if (s && !modular && hitPartGone(s, k)) continue;   // partial destruction step 4: a marked part at 0 is gone, its shape too
          const { tx, tz } = partFrame(s, up, k);
          const at = (x: number, y: number, z: number) => vecAdd(base, vecAdd(vecScale(tx, x * scale), vecAdd(vecScale(up, y * scale * ruin), vecScale(tz, z * scale))));
          const p = partPrim(def.parts[k], at, tx, up, tz, scale, ruin);
          prims.push(p);
          if (p.cyl) { reach = Math.max(reach, horizontal(vecSub(p.base, base), up) + Math.max(p.r0, p.r1)); top = Math.max(top, p.h1); }
          else { reach = Math.max(reach, horizontal(vecSub(p.c, base), up) + Math.sqrt(p.hx * p.hx + p.hz * p.hz)); top = Math.max(top, vecDot(vecSub(p.c, base), up) + p.hy); }
        }
      } else if (o.pieces) {
        // E5.2: a tree's wood as drawn — each piece a frustum along its own axis (a leaning trunk, a limb, a trunk lying
        // on the ground), from its foot end; the test above is the same along any axis
        for (const pc of o.pieces) {
          const axis = vecSub(pc.b, pc.a), L = vecLength(axis);
          if (!(L > 0)) continue;
          prims.push({ cyl: true, base: vecAdd(base, pc.a), up: vecScale(axis, 1 / L), r0: pc.ra, r1: pc.rb, h0: 0, h1: L });
          reach = Math.max(reach, horizontal(pc.a, up) + pc.ra, horizontal(pc.b, up) + pc.rb);
          top = Math.max(top, vecDot(pc.a, up) + pc.ra, vecDot(pc.b, up) + pc.rb);
          bottom = Math.min(bottom, vecDot(pc.a, up) - pc.ra, vecDot(pc.b, up) - pc.rb);
        }
      } else if (o.profile) {
        // 67.13: the drawn rock narrows with height — a frustum per tenth, its width the profile's at either end (the
        // drawn rock starts `sink` of its height under the ground, so the first tenths may be cut at the ground)
        const k = o.profile.length, sink = o.sink ?? 0;
        for (let i = 0; i + 1 < k; i++) {
          let h0 = (i / (k - 1) - sink) * o.height;
          const h1 = ((i + 1) / (k - 1) - sink) * o.height;
          let r0 = o.radius * o.profile[i];
          const r1 = o.radius * o.profile[i + 1];
          if (h1 <= 0 || (r0 <= 0 && r1 <= 0)) continue;
          if (h0 < 0) { r0 += (r1 - r0) * (-h0 / (h1 - h0)); h0 = 0; }
          prims.push({ cyl: true, base, up, r0, r1, h0, h1 });
          reach = Math.max(reach, r0, r1);
          top = Math.max(top, h1);
        }
      } else {
        prims.push({ cyl: true, base, up, r0: o.radius, r1: o.radius, h0: 0, h1: o.height });
        reach = o.radius; top = o.height;
      }
    if (prims.length === 0) return null;
    const sub = modular ? buildSub(prims, base, person) : null;   // 63.14 step 4: the modules by cell
    return { sh: { ref: o.ref, dir: up, prims, reach, ...((o.pieces || o.profile || modular) && prims.length > 1 ? { bound: { cyl: true as const, base, up, r0: reach, r1: reach, h0: bottom, h1: top } } : {}), ...(sub ? { sub } : {}), ...(modular ? { modular: true as const } : {}) }, b: { reach, top, bottom, base, up } };
  };
  // the tiers, the filing and the index over a list of shapes and their bounds (the full build's, or a patched one's)
  const file = (list: HitShape[], bounds: ShapeBound[], src: WalkObstacle[], shapeAt: Int32Array,
    /** E5.4's tail patch: the 90th percentiles found by selection, and each shape's cell at `n0` cells per edge */
    pre?: { mReach: number; mTop: number; cells: Uint32Array; n0: number }): PlanetShapes => {
    // two tiers: the ordinary shapes on the cells, the few large ones beside them (each by its bounding sphere)
    // (63.4 pd step 1: numeric typed sorts — the same order as the comparator's, a fraction of its time at 2,000 shapes)
    const p90 = (s: Float64Array) => { s.sort(); return s.length ? s[Math.floor((s.length - 1) * 0.9)] : 0; };
    const mReach = pre ? pre.mReach : p90(Float64Array.from(bounds, b => b.reach)), mTop = pre ? pre.mTop : p90(Float64Array.from(bounds, b => b.top));
    const filed: number[] = [], large: PlanetShapes['large'] = [];
    let maxReach = 0, maxTop = 0;
    bounds.forEach((b, i) => {
      if (b.reach > 4 * mReach || b.top > 4 * mTop) {
        large.push(largeSphere(i, b));
        return;
      }
      filed.push(i);
      maxReach = Math.max(maxReach, b.reach); maxTop = Math.max(maxTop, b.top);
    });
    const n = po.index.n;
    // 62.56: the same shapes' directions filed in the same order — the last index as it is
    const sameDir = (a: Vec3, b: Vec3) => a === b || (a.x === b.x && a.y === b.y && a.z === b.z);
    const sameFiling = !pre && same && same.shapes.index.n === n && same.shapes.filed.length === filed.length && filed.every((i, k) => sameDir(list[i].dir, same.shapes.list[same.shapes.filed[k]].dir));
    // (E5.4's tail patch: every shape's cell kept beside the index — the patch's own, or found here)
    const cells = pre && pre.n0 === n ? pre.cells : cellsOfItems(list.length, i => list[i].dir, n);
    const value: PlanetShapes = {
      list, index: sameFiling ? same!.shapes.index : fileCellIndex(Uint32Array.from(filed, i => cells[i]), n), filed: Int32Array.from(filed), maxReach, maxTop, large, maxTopAll: bounds.reduce((m, b) => Math.max(m, b.top), 0),
      stepLen: Math.max(2 * maxReach, (2 * planet.radius) / n / 4), stamp: new Uint32Array(list.length), round: 0,
    };
    shapeOfObstacle.set(value, src);
    lastShapes.set(sk, { shapes: value, src, bounds, heightmap: planet.heightmap, edits: fineEditsOf(planet), basePositions: planet.basePositions, radius: planet.radius, position: planet.position, config: state.config, poList: po.list, poIndex: po.index, shapeAt, mReach, mTop, cells });
    return value;
  };
  // 67.45: a crater under the same filing (surfaceWalk.ts planetObstacles' crater path: the list kept but a felled tree's
  // stump made again in its slot) — the shapes over moved ground and in changed slots are built again, the rest kept in
  // place; a shape's reach and top do not read the ground, so its tier, the filing and the index stay as they were (the
  // cost is the moved shapes', not the planet's)
  if (same && moved && po.index === same.poIndex && po.list.length === same.poList.length) {
    const redo = new Set(moved);
    if (po.list !== same.poList) for (let i = 0; i < po.list.length; i++) if (po.list[i] !== same.poList[i]) redo.add(i);
    const list2 = same.shapes.list.slice(), bounds2 = same.bounds.slice();
    let large = same.shapes.large, src2 = same.src, ok = same.mReach !== undefined, maxes = false, retier = false;
    const redoShapes: number[] = [];
    const mR = same.mReach!, mT = same.mTop!, side = (a: number, b: number, p: number) => (a < p && b < p) || (a > p && b > p);
    for (const oi of ok ? redo : []) {
      const si = same.shapeAt[oi], o = po.list[oi];
      if (si < 0) { if (o.ref && po.list[oi] !== same.poList[oi]) { ok = false; break; } continue; }
      if (!o.ref) { ok = false; break; }
      const made = shapeFor(o as WalkObstacle & { ref: ObstacleRef });
      if (!made) { ok = false; break; }
      const ob = same.bounds[si], nb = made.b;
      if (nb.reach !== ob.reach || nb.top !== ob.top) {
        // (a stump made again over moved ground lies a hair differently — its fall's way turns with the ground: its reach
        // and top may move in the last digits; a felled tree stands low and lies long) the 90th percentiles stand while it
        // stays on the same side of each, its tier while that does, and the maxima are found again below; else the patched
        // list is tiered and filed again as a full build would
        const wasLarge = ob.reach > 4 * mR || ob.top > 4 * mT, isLarge = nb.reach > 4 * mR || nb.top > 4 * mT;
        if (!side(ob.reach, nb.reach, mR) || !side(ob.top, nb.top, mT) || wasLarge !== isLarge) retier = true;
        maxes = true;
      }
      list2[si] = made.sh; bounds2[si] = made.b; redoShapes.push(si);
      if (src2[si] !== o) { if (src2 === same.src) src2 = src2.slice(); src2[si] = o; }
      const li = large.findIndex(e => e.i === si);
      if (li >= 0) {
        if (large === same.shapes.large) large = large.slice();
        large[li] = largeSphere(si, made.b);
      }
    }
    if (ok && retier) {
      // (E5.4, fellings in the wood: tiered and filed again as file() does, the 90th percentiles found by selection — the
      // sort's own numbers — and the index from the kept cells, re-made only for the shapes made again)
      const reaches = Float64Array.from(bounds2, b => b.reach), tops = Float64Array.from(bounds2, b => b.top);
      if (same.cells && same.cells.length === list2.length && allFinite(reaches) && allFinite(tops)) {
        const at = (a: Float64Array) => (a.length ? kthOfSort(a, Math.floor((a.length - 1) * 0.9)) : 0);
        const cells2 = same.cells.slice();
        for (const si of redoShapes) cells2[si] = cellOf(list2[si].dir.x, list2[si].dir.y, list2[si].dir.z, same.poIndex.n);
        simWork.tableCraterPatches++;
        return file(list2, bounds2, src2, same.shapeAt, { mReach: at(reaches), mTop: at(tops), cells: cells2, n0: same.poIndex.n });
      }
      simWork.tableCraterPatches++;
      return file(list2, bounds2, src2, same.shapeAt);
    }
    if (ok) {
      const value: PlanetShapes = { ...same.shapes, list: list2, large, stamp: new Uint32Array(list2.length), round: 0, byStructure: undefined };
      if (maxes) {
        let maxReach = 0, maxTop = 0, maxTopAll = 0;
        for (const i of value.filed) { maxReach = Math.max(maxReach, bounds2[i].reach); maxTop = Math.max(maxTop, bounds2[i].top); }
        for (const b of bounds2) maxTopAll = Math.max(maxTopAll, b.top);
        Object.assign(value, { maxReach, maxTop, maxTopAll, stepLen: Math.max(2 * maxReach, (2 * planet.radius) / po.index.n / 4) });
      }
      simWork.tableCraterPatches++;
      shapeOfObstacle.set(value, src2);
      lastShapes.set(sk, { ...same, shapes: value, src: src2, bounds: bounds2, heightmap: planet.heightmap, edits: fineEditsOf(planet), poList: po.list });
      return value;
    }
  }
  // E5.4's tail patch: the obstacles were patched for forest cells joining and leaving (surfaceWalk.ts obstacleSplice) over
  // the list these shapes were built from, on the same ground: the kept shapes in their order, the joined obstacles' made
  // where they went in; the tiers, the filing and the index as file() makes them, the percentiles found by selection
  const spl = obstacleSplice(po);
  if (same && moved && moved.size === 0 && same.heightmap === planet.heightmap && spl && spl.prev.list === same.poList && same.cells && same.cells.length === same.shapes.list.length) {
    const n0 = same.poIndex.n, cap = po.list.length, list2: HitShape[] = [], bounds2: ShapeBound[] = [], src2: WalkObstacle[] = [];
    const cells2 = new Uint32Array(cap), reachAll = new Float64Array(cap), topAll = new Float64Array(cap);
    const shapeAt2 = new Int32Array(cap).fill(-1);
    let oi = 0, m = 0;
    for (let ni = 0; ni < cap; ni++) {
      let sh: HitShape, b: ShapeBound, o: WalkObstacle, c: number;
      if (ni >= spl.at && ni < spl.at + spl.count) {
        o = po.list[ni];
        if (!o.ref) continue;
        const made = shapeFor(o as WalkObstacle & { ref: ObstacleRef });
        if (!made) continue;
        sh = made.sh; b = made.b; c = cellOf(made.sh.dir.x, made.sh.dir.y, made.sh.dir.z, n0);
      } else {
        while (spl.gone[oi]) oi++;
        const was = spl.prev.list[oi], si = same.shapeAt[oi++];
        o = po.list[ni];
        if (o !== was) {
          // (an obstacle re-made in its slot beside the join — a structure replaced in place: its shape made again)
          if (!o.ref) continue;
          const made = shapeFor(o as WalkObstacle & { ref: ObstacleRef });
          if (!made) continue;
          sh = made.sh; b = made.b; c = cellOf(made.sh.dir.x, made.sh.dir.y, made.sh.dir.z, n0);
        } else {
          if (si < 0) continue;
          sh = same.shapes.list[si]; b = same.bounds[si]; o = same.src[si]; c = same.cells[si];
        }
      }
      shapeAt2[ni] = m; list2.push(sh); bounds2.push(b); src2.push(o); cells2[m] = c; reachAll[m] = b.reach; topAll[m] = b.top; m++;
    }
    const reaches = reachAll.slice(0, m), tops = topAll.slice(0, m);
    if (allFinite(reaches) && allFinite(tops)) {
      const at = (a: Float64Array) => (a.length ? kthOfSort(a, Math.floor((a.length - 1) * 0.9)) : 0);
      simWork.tableJoinPatches++;
      const patched = file(list2, bounds2, src2, shapeAt2, { mReach: at(reaches), mTop: at(tops), cells: cells2.slice(0, m), n0 });
      shapesPatchedFrom.set(patched, same.shapes);
      return patched;
    }
    simWork.tableJoinFallbacks++;
  }
  const shapeAt = new Int32Array(po.list.length).fill(-1);
  simWork.tableBuilds++;
  let kept = 0;
  for (let oi = 0; oi < po.list.length; oi++) {
    const o = po.list[oi];
    if (!o.ref) continue;
    const kept0 = keepOf(), k = kept0 && !moved!.has(oi) ? kept0.get(o) : undefined;
    if (k !== undefined) { shapeAt[oi] = list.length; list.push(same!.shapes.list[k]); bounds.push(same!.bounds[k]); src.push(o); kept++; continue; }
    const made = shapeFor(o as WalkObstacle & { ref: ObstacleRef });
    if (!made) continue;
    shapeAt[oi] = list.length; list.push(made.sh); bounds.push(made.b); src.push(o);
  }
  // 62.56: every shape kept, in the same order — the last shapes' tiers and index as they are
  if (same && kept === list.length && list.length === same.shapes.list.length && list.every((sh, i) => sh === same.shapes.list[i])) {
    const value: PlanetShapes = { ...same.shapes, stamp: new Uint32Array(list.length), round: 0, byStructure: undefined };
    shapeOfObstacle.set(value, src);
  lastShapes.set(sk, { shapes: value, src, bounds, heightmap: planet.heightmap, edits: fineEditsOf(planet), basePositions: planet.basePositions, radius: planet.radius, position: planet.position, config: state.config, poList: po.list, poIndex: po.index, shapeAt, mReach: same.mReach, mTop: same.mTop, cells: same.cells });
    return value;
  }
  return file(list, bounds, src, shapeAt);
}

/**
 * Partial destruction step 4: how far `pt` is from each of a standing structure's marked parts (core/parts.ts), in
 * STRUCTURE_PARTS' order — from the part's own shape, placed as buildShapes places it (Infinity for a part already gone).
 * Null when it has no marked parts or no planet.
 */
export function markedPartDistances(pt: Vec3, s: StructureState, state: Pick<GameState, 'planets' | 'config'>): number[] | null {
  const marked = markedParts(s.type), planet = state.planets[s.planetIndex];
  if (marked.length === 0 || !planet || s.health <= 0) return null;
  const def = structureHitbox(s, state.config);
  const person = PERSON_BODY_HEIGHT * personSetOf(state).k;
  const scale = def.ruler === 'prop' ? surfacePropScale(planet.radius) : def.ruler === 'unit' ? PERSON_BODY_HEIGHT : person;
  const up = vecNormalize(s.surfaceDir);
  const base = vecAdd(planet.position, vecScale(up, getSurfaceRadius(planet, up) + (s.baseLift ?? 0)));   // (67.52)
  return marked.map((m, k) => {
    if ((s.parts && s.parts[k] <= 0) || !def.parts[m.part]) return Infinity;
    const { tx, tz } = partFrame(s, up, m.part);   // (63.1f's follow-up: a turret's part where the turret turned it)
    const at = (x: number, y: number, z: number) => vecAdd(base, vecAdd(vecScale(tx, x * scale), vecAdd(vecScale(up, y * scale), vecScale(tz, z * scale))));
    return vecLength(vecSub(pt, primNearest(pt, partPrim(def.parts[m.part], at, tx, up, tz, scale, 1))));
  });
}

/**
 * Partial destruction step 5 (the structures' pieces): a standing structure's `k`-th marked part as it stands — its middle
 * (world), how high that is over the structure's foot, its offset across the ground from the foot, and its size as a
 * lying piece (the longest of its extents, and the round of the other two). Null with no such part or planet.
 */
export function markedPartPiece(s: StructureState, k: number, state: Pick<GameState, 'planets' | 'config'>): { foot: Vec3; up: Vec3; height: number; across: Vec3; length: number; radius: number } | null {
  const m = markedParts(s.type)[k], planet = state.planets[s.planetIndex];
  if (!m || !planet) return null;
  const def = structureHitbox(s, state.config), part = def.parts[m.part];
  if (!part) return null;
  const person = PERSON_BODY_HEIGHT * personSetOf(state).k;
  const scale = def.ruler === 'prop' ? surfacePropScale(planet.radius) : def.ruler === 'unit' ? PERSON_BODY_HEIGHT : person;
  const up = vecNormalize(s.surfaceDir);
  const foot = vecAdd(planet.position, vecScale(up, getSurfaceRadius(planet, up) + (s.baseLift ?? 0)));   // (67.52)
  const { tx, tz } = partFrame(s, up, m.part);   // (63.1f's follow-up: a turret's part falls from where the turret turned it)
  const at = (x: number, y: number, z: number) => vecAdd(foot, vecAdd(vecScale(tx, x * scale), vecAdd(vecScale(up, y * scale), vecScale(tz, z * scale))));
  const p = partPrim(part, at, tx, up, tz, scale, 1);
  const mid = p.cyl ? vecAdd(p.base, vecScale(p.up, (p.h0 + p.h1) / 2)) : p.c;
  const ext = p.cyl ? [p.r0, p.r0, (p.h1 - p.h0) / 2] : [p.hx, p.hy, p.hz];
  ext.sort((a, b) => b - a);
  const rel = vecSub(mid, foot), height = Math.max(0, vecDot(rel, up));
  return { foot, up, height, across: vecSub(rel, vecScale(up, vecDot(rel, up))), length: 2 * ext[0], radius: Math.sqrt(ext[1] * ext[2]) };
}

/**
 * 65.22 (2): the frame a structure stands in — the renderer's upright frame, turned by the structure's own `yaw` (its front
 * toward tx by it) when it has one. Every part stands in it (a turret's parts turned further by turretYaw: partFrame).
 */
export function structureFrame(s: Partial<Pick<StructureState, 'yaw'>> | undefined, up: Vec3): { tx: Vec3; tz: Vec3 } {
  const f = uprightFrame(up);
  if (!s?.yaw) return f;
  const c = dcos(s.yaw), sn = dsin(s.yaw);
  return {
    tx: { x: f.tx.x * c - f.tz.x * sn, y: f.tx.y * c - f.tz.y * sn, z: f.tx.z * c - f.tz.z * sn },
    tz: { x: f.tx.x * sn + f.tz.x * c, y: f.tx.y * sn + f.tz.y * c, z: f.tx.z * sn + f.tz.z * c },
  };
}

/** 65.22 (2): the yaw that turns a structure standing at `up` to face `dir` (a direction along the ground there). */
export function yawFacing(up: Vec3, dir: Vec3): number {
  const f = uprightFrame(up);
  return datan2(vecDot(dir, f.tx), vecDot(dir, f.tz));
}

/**
 * 63.1f's follow-up: the frame a structure's part `k` stands in — the renderer's upright frame (uprightFrame), turned by the
 * structure's `turretYaw` about its axis for a part on a turret (TURRET_PARTS_FROM: the gun pit's all but its plinth) while
 * it stands, as the drawn turret turns (+Z, the gun, toward tx by the yaw). A ruin's parts stay as built (its wreck is drawn so).
 */
export function partFrame(s: Pick<StructureState, 'type' | 'turretYaw' | 'health'> & Partial<Pick<StructureState, 'yaw'>> | undefined, up: Vec3, k: number): { tx: Vec3; tz: Vec3 } {
  const f = structureFrame(s, up);
  const from = s ? TURRET_PARTS_FROM[s.type as BodyKey] : undefined;
  if (!s || from === undefined || k < from || !s.turretYaw || s.health <= 0) return f;
  const c = dcos(s.turretYaw), sn = dsin(s.turretYaw);
  return {
    tx: { x: f.tx.x * c - f.tz.x * sn, y: f.tx.y * c - f.tz.y * sn, z: f.tx.z * c - f.tz.z * sn },
    tz: { x: f.tx.x * sn + f.tz.x * c, y: f.tx.y * sn + f.tz.y * c, z: f.tx.z * sn + f.tz.z * c },
  };
}

/** A part of a measured shape in the world: a box as it is; a capsule (upright in the tables) as the cylinder round it. */
function partPrim(part: HitPart, at: (x: number, y: number, z: number) => Vec3, tx: Vec3, up: Vec3, ty: Vec3, scale: number, ruin: number): Prim {
  if (part.shape === 'box') {
    const b: Prim = { cyl: false, c: at(part.centre[0], part.centre[1], part.centre[2]), ax: tx, ay: up, az: ty, hx: part.half[0] * scale, hy: part.half[1] * scale * ruin, hz: part.half[2] * scale };
    if (part.pass) b.pass = true;
    return b;
  }
  const lo = Math.min(part.a[1], part.b[1]) - part.radius, hi = Math.max(part.a[1], part.b[1]) + part.radius;
  return { cyl: true, base: at(part.a[0], 0, part.a[2]), up, r0: part.radius * scale, r1: part.radius * scale, h0: Math.max(0, lo) * scale * ruin, h1: hi * scale * ruin };
}

/** The length of `v`'s part across `up`. */
function horizontal(v: Vec3, up: Vec3): number {
  const a = vecDot(v, up);
  const x = v.x - up.x * a, y = v.y - up.y * a, z = v.z - up.z * a;
  return Math.sqrt(x * x + y * y + z * z);
}

// ---------------------------------------------------------------------------
// The tests
// ---------------------------------------------------------------------------

/**
 * Where along a→b (0…1) the segment first enters `p`, or null. A segment that starts inside `p` is not stopped by it:
 * you see and shoot out of what you stand in (a tower's eye in its radar head, a gunner's in the gun's shield) — and
 * walking keeps people out of props, so nothing else starts inside one.
 */
export function segmentPrim(a: Vec3, b: Vec3, p: Prim): number | null {
  if (pointInPrim(a, p)) return null;
  return segmentEnters(a, b, p);
}

/**
 * E5.2: might a→b meet shape `sh`? 0: no (it has a bound the segment neither starts in nor enters); 1: it may, and the
 * segment starts outside the bound — so outside every part, and each part needs only the entry test; 2: test it whole.
 */
export function mayMeet(a: Vec3, b: Vec3, sh: HitShape): 0 | 1 | 2 {
  if (!sh.bound) return 2;
  if (pointInPrim(a, sh.bound)) return 2;
  return segmentEnters(a, b, sh.bound) === null ? 0 : 1;
}
/** A part's entry along a→b, given what mayMeet said (1: the start is outside it already). */
export function partEntry(a: Vec3, b: Vec3, p: Prim, meet: 1 | 2): number | null {
  if (!p.cyl && p.pass) return null;   // 63.14 step 3: rays pass wire (the walk is held: outlineDiscs keeps it)
  return meet === 1 ? segmentEnters(a, b, p) : segmentPrim(a, b, p);
}

/** segmentPrim's test for a segment that does not start inside `p`. */
function segmentEnters(a: Vec3, b: Vec3, p: Prim): number | null {
  hitWork.prims++;
  let lo = 0, hi = 1;
  // scalars, not vectors (62.49: a vector a test was a hot allocation); vecSub / vecDot's arithmetic, in their order
  const ddx = b.x - a.x, ddy = b.y - a.y, ddz = b.z - a.z;
  if (p.cyl) {
    const u = p.up;
    const ssx = a.x - p.base.x, ssy = a.y - p.base.y, ssz = a.z - p.base.z;
    const al = ssx * u.x + ssy * u.y + ssz * u.z, dl = ddx * u.x + ddy * u.y + ddz * u.z;
    // along the axis: between h0 and h1
    if (Math.abs(dl) < 1e-15) { if (al < p.h0 || al > p.h1) return null; }
    else {
      let t0 = (p.h0 - al) / dl, t1 = (p.h1 - al) / dl;
      if (t0 > t1) { const t = t0; t0 = t1; t1 = t; }
      if (t0 > lo) lo = t0;
      if (t1 < hi) hi = t1;
      if (lo > hi) return null;
    }
    // across it: within the radius at that height, r(al) = r0 + g (al − h0) — f(t) = |radial(t)|² − r(t)² ≤ 0, a
    // quadratic in t; the entry is lo if f(lo) ≤ 0, else the first root after lo (f is continuous)
    const g = p.h1 > p.h0 ? (p.r1 - p.r0) / (p.h1 - p.h0) : 0;
    const rA = p.r0 + g * (al - p.h0), rB = g * dl;
    const sx = ssx - u.x * al, sy = ssy - u.y * al, sz = ssz - u.z * al;
    const dx = ddx - u.x * dl, dy = ddy - u.y * dl, dz = ddz - u.z * dl;
    const qa = dx * dx + dy * dy + dz * dz - rB * rB, qb = 2 * (sx * dx + sy * dy + sz * dz - rA * rB), qc = sx * sx + sy * sy + sz * sz - rA * rA;
    if ((qa * lo + qb) * lo + qc <= 0) return lo;
    let first = Infinity;
    if (Math.abs(qa) < 1e-24) { if (qb < -1e-24) first = -qc / qb; }   // linear: f falls to 0 only if it is falling
    else {
      const disc = qb * qb - 4 * qa * qc;
      if (disc < 0) return null;
      const q = Math.sqrt(disc);
      let t0 = (-qb - q) / (2 * qa), t1 = (-qb + q) / (2 * qa);
      if (t0 > t1) { const t = t0; t0 = t1; t1 = t; }
      first = t0 > lo ? t0 : t1 > lo ? t1 : Infinity;
    }
    return first <= hi ? first : null;
  }
  const ssx = a.x - p.c.x, ssy = a.y - p.c.y, ssz = a.z - p.c.z;
  for (let k = 0; k < 3; k++) {
    const axis = k === 0 ? p.ax : k === 1 ? p.ay : p.az, h = k === 0 ? p.hx : k === 1 ? p.hy : p.hz;
    const sa = ssx * axis.x + ssy * axis.y + ssz * axis.z, da = ddx * axis.x + ddy * axis.y + ddz * axis.z;
    if (Math.abs(da) < 1e-15) { if (sa < -h || sa > h) return null; continue; }
    let t0 = (-h - sa) / da, t1 = (h - sa) / da;
    if (t0 > t1) { const t = t0; t0 = t1; t1 = t; }
    if (t0 > lo) lo = t0;
    if (t1 < hi) hi = t1;
    if (lo > hi) return null;
  }
  return lo;
}

/** Is `pt` inside `p`? */
export function pointInPrim(pt: Vec3, p: Prim): boolean {
  hitWork.prims++;
  if (p.cyl) {
    // scalars (62.49): vecSub / vecDot / horizontal's arithmetic, in their order
    const u = p.up, sx = pt.x - p.base.x, sy = pt.y - p.base.y, sz = pt.z - p.base.z, al = sx * u.x + sy * u.y + sz * u.z;
    if (al < p.h0 || al > p.h1) return false;
    const g = p.h1 > p.h0 ? (p.r1 - p.r0) / (p.h1 - p.h0) : 0;
    const x = sx - u.x * al, y = sy - u.y * al, z = sz - u.z * al;
    return Math.sqrt(x * x + y * y + z * z) <= p.r0 + g * (al - p.h0);
  }
  const sx = pt.x - p.c.x, sy = pt.y - p.c.y, sz = pt.z - p.c.z;
  return Math.abs(sx * p.ax.x + sy * p.ax.y + sz * p.ax.z) <= p.hx && Math.abs(sx * p.ay.x + sy * p.ay.y + sz * p.ay.z) <= p.hy && Math.abs(sx * p.az.x + sy * p.az.y + sz * p.az.z) <= p.hz;
}

/**
 * The shapes a path through `points` (world, near the planet) could meet, as indices into `shapes.list`: the cells
 * round the points (a cell apart along a segment), each query widened by half the gap to the next point and the
 * widest shape, over the nearest the path comes to the centre less the tallest shape. `out` is filled and returned.
 */
export function shapesNear(points0: readonly Vec3[], planet: PlanetState, shapes: PlanetShapes, out: number[] = []): number[] {
  out.length = 0;
  const all = shapes.list.length;
  if (all === 0) return out;
  // query no closer than a step apart (a bullet's tick of substeps is one query), the gap between queries widening each
  // (scalars, not vectors — 62.49: these were a hot allocation; each distance is vecLength(vecSub(…))'s arithmetic)
  const points: Vec3[] = [points0[0]];
  for (let i = 1; i < points0.length; i++) {
    const last = points[points.length - 1];
    if (dist(points0[i], last) >= shapes.stepLen || i === points0.length - 1) points.push(points0[i]);
  }
  let nearest = Infinity, gap = 0;
  for (const p of points0) nearest = Math.min(nearest, dist(p, planet.position));
  for (let i = 1; i < points.length; i++) gap = Math.max(gap, dist(points[i], points[i - 1]));
  const floor = nearest - shapes.maxTop - gap;
  if (all < 32 || !(floor > 0)) { for (let i = 0; i < all; i++) out.push(i); hitWork.candidates += all; return out; }
  // a chord of length L between directions at distances ≥ floor spans at most 2 asin(L / 2 floor) ≤ 1.1 L / floor here
  const angle = (1.1 * (gap / 2 + shapes.maxReach)) / floor + 1e-9;
  const round = ++shapes.round === 0xffffffff ? (shapes.stamp.fill(0), shapes.round = 1) : shapes.round;
  for (const p of points) {
    hitWork.queries++;
    const qc = queryCellIndexCount(shapes.index, unitFrom(p, planet.position), angle, false), qids = cellQueryIds();   // (62.57: the cells' buffer, read at once)
    for (let q = 0; q < qc; q++) {
      const i = shapes.filed[qids[q]];
      if (shapes.stamp[i] === round) continue;
      shapes.stamp[i] = round;
      out.push(i);
    }
  }
  // the large shapes: those whose bounding sphere comes within reach of the path
  for (const L of shapes.large) {
    let met = points.length === 1 ? dist(points[0], L.c) <= L.r : false;
    for (let k = 1; k < points.length && !met; k++) met = pointSegmentDistance(L.c, points[k - 1], points[k]) <= L.r;
    if (met) out.push(L.i);
  }
  hitWork.candidates += out.length;
  return out;
}

/** How far `c` is from the segment a→b (scalars: the vector helpers' arithmetic, in their order — 62.49). */
function pointSegmentDistance(c: Vec3, a: Vec3, b: Vec3): number {
  const abx = b.x - a.x, aby = b.y - a.y, abz = b.z - a.z, l2 = abx * abx + aby * aby + abz * abz;
  const t = l2 > 0 ? Math.max(0, Math.min(1, ((c.x - a.x) * abx + (c.y - a.y) * aby + (c.z - a.z) * abz) / l2)) : 0;
  const px = c.x - (a.x + abx * t), py = c.y - (a.y + aby * t), pz = c.z - (a.z + abz * t);
  return Math.sqrt(px * px + py * py + pz * pz);
}

/** |a − b| — vecLength(vecSub(a, b))'s arithmetic, with no vector made. */
function dist(a: Vec3, b: Vec3): number {
  const x = a.x - b.x, y = a.y - b.y, z = a.z - b.z;
  return Math.sqrt(x * x + y * y + z * z);
}

const unitScratch = { x: 0, y: 0, z: 1 };
/** vecNormalize(vecSub(p, o))'s arithmetic into one reused vector (read at once by the cell query, never kept). */
function unitFrom(p: Vec3, o: Vec3): Vec3 {
  const x = p.x - o.x, y = p.y - o.y, z = p.z - o.z, len = Math.sqrt(x * x + y * y + z * z);
  if (len < 1e-10) { unitScratch.x = 0; unitScratch.y = 0; unitScratch.z = 0; return unitScratch; }
  const k = 1 / len;
  unitScratch.x = x * k; unitScratch.y = y * k; unitScratch.z = z * k;
  return unitScratch;
}

/** Points `stepLen` apart along a→b (both ends included). */
export function alongSegment(a: Vec3, b: Vec3, stepLen: number): Vec3[] {
  const d = vecSub(b, a), n = Math.max(1, Math.ceil(vecLength(d) / Math.max(1e-9, stepLen)));
  const out: Vec3[] = [];
  for (let k = 0; k <= n; k++) out.push(vecAdd(a, vecScale(d, k / n)));
  return out;
}

/**
 * The plane through the planet's centre and a→b: every point of the segment lies in it, so a shape whose direction is
 * further from it than its reach over the segment's floor (the nearest it comes to the centre, less the shape's top)
 * cannot be met — one dot product instead of its parts' tests. Null when the segment points through the centre.
 */
function segmentPlane(a: Vec3, b: Vec3, planet: PlanetState): { m: Vec3; closest: number } | null {
  const ra = vecSub(a, planet.position), rb = vecSub(b, planet.position);
  const mx = ra.y * rb.z - ra.z * rb.y, my = ra.z * rb.x - ra.x * rb.z, mz = ra.x * rb.y - ra.y * rb.x;
  const ml = Math.sqrt(mx * mx + my * my + mz * mz);
  if (ml < 1e-9 * vecLength(ra) * vecLength(rb)) return null;
  const ab = vecSub(rb, ra), t = Math.max(0, Math.min(1, -vecDot(ra, ab) / Math.max(1e-18, vecDot(ab, ab))));
  return { m: { x: mx / ml, y: my / ml, z: mz / ml }, closest: vecLength(vecAdd(ra, vecScale(ab, t))) };
}
function offPlane(sh: HitShape, top: number, plane: { m: Vec3; closest: number } | null): boolean {
  if (!plane) return false;
  const floor = plane.closest - top;
  return floor > 0 && Math.abs(vecDot(sh.dir, plane.m)) > (sh.reach / floor) * 1.1 + 1e-9;
}

/** The first of `candidates` (indices) a→b meets: where along it (0…1) and what. */
export function firstHit(a: Vec3, b: Vec3, shapes: PlanetShapes, candidates: readonly number[], planet?: PlanetState): { t: number; ref: ObstacleRef } | null {
  let best: { t: number; ref: ObstacleRef } | null = null;
  const plane = planet && candidates.length > 8 ? segmentPlane(a, b, planet) : null;
  for (const i of candidates) {
    const sh = shapes.list[i];
    if (offPlane(sh, shapes.maxTopAll, plane)) continue;
    const meet = mayMeet(a, b, sh);
    if (meet === 0) continue;
    if (sh.sub) {   // 63.14 step 4: the prims of the cells the segment passes
      for (const k of subPrims(sh, a, b)) { const t = partEntry(a, b, sh.prims[k], meet); if (t !== null && (!best || t < best.t)) best = { t, ref: sh.ref }; }
      continue;
    }
    for (const p of sh.prims) {
      const t = partEntry(a, b, p, meet);
      if (t !== null && (!best || t < best.t)) best = { t, ref: sh.ref };
    }
  }
  return best;
}

/** Does a→b meet any shape on planet `planetIndex`? */
export function segmentBlocked(a: Vec3, b: Vec3, planetIndex: number, state: Pick<GameState, 'rocks' | 'flora' | 'structures' | 'planets'> & Partial<Pick<GameState, 'config'>>): boolean {
  if (segmentBlockedStanding(a, b, planetIndex, state)) return true;
  return segmentBlockedMoving(a, b, planetIndex, state);
}
/**
 * 62.60 (the look's cost): what moves across a segment — a driving hull (63.1d step 2: it blocks as it stands), a ship (69.3b) — tested
 * fresh each time; with `segmentBlockedStanding` it is `segmentBlocked`, the standing half a pure function of the ends and
 * the planet's shapes (planetShapes, memoised on the walk obstacles) that a caller may keep across ticks.
 */
export function segmentBlockedMoving(a: Vec3, b: Vec3, planetIndex: number, state: Pick<GameState, 'rocks' | 'flora' | 'structures' | 'planets'> & Partial<Pick<GameState, 'config'>>): boolean {
  // 69.3b, the ship as a body: a ship across the segment stands in the way (a sight, a cover test); nothing with the switch
  // off. In the moving half: a flight moves every tick and a parked ship leaves and comes back, so it is never memoised
  if (state.config?.shipsDestructible && shipBlocks(a, b, planetIndex, state as GameState)) return true;
  return firstMovingHit(a, b, planetIndex, state) !== null;
}
/** The standing shapes across a→b on the planet (the rocks, the trees' wood, the structures, a parked hull): none of what moves. */
export function segmentBlockedStanding(a: Vec3, b: Vec3, planetIndex: number, state: Pick<GameState, 'rocks' | 'flora' | 'structures' | 'planets'> & Partial<Pick<GameState, 'config'>>): boolean {
  const planet = state.planets[planetIndex];
  const shapes = planetShapes(state, planetIndex);
  if (shapes.list.length === 0) return false;
  const near = shapesNear(alongSegment(a, b, shapes.stepLen), planet, shapes, nearScratch);
  const plane = near.length > 8 ? segmentPlane(a, b, planet) : null;
  for (const i of near) {
    const sh = shapes.list[i];
    if (offPlane(sh, shapes.maxTopAll, plane)) continue;
    const meet = mayMeet(a, b, sh);
    if (meet === 0) continue;
    if (sh.sub) { for (const k of subPrims(sh, a, b)) if (partEntry(a, b, sh.prims[k], meet) !== null) return true; continue; }   // 63.14 step 4
    for (const p of sh.prims) if (partEntry(a, b, p, meet) !== null) return true;
  }
  return false;
}
const nearScratch: number[] = [];

/**
 * 69.15 (the owner: "Great, good future item"): what a sight line can't see through — a standing building (the planet's
 * buildings by their hitbox: the HQ, a house, the motor pool), a ship's wreck, a set-down container, a vehicle frame, and a
 * ruin's walls. A vehicle moves (its hull is no wall to a line asked across ticks), a gun or a prop is cover, not a wall.
 */
export function blocksSight(s: Pick<StructureState, 'type' | 'health'>): boolean {
  return s.health > 0 && (isPlanetBuilding(s.type) || s.type === 'shipWreck' || s.type === 'dropContainer' || s.type === 'frame');
}
let lastWalls: { from: readonly StructureState[]; walls: readonly StructureState[] } | null = null;
/** 69.15: each planet's shapes' walls (their indices in the list), for the wall list they were picked for. */
const wallShapes = new WeakMap<PlanetShapes, { walls: readonly StructureState[]; idx: number[] }>();
/**
 * 69.15: the structures that block sight (blocksSight), as one list that keeps its identity while they do — the same
 * objects in the same order — so a cache of sight lines (ai/actionBot.ts's memo) asks only whether this changed.
 */
export function sightWalls(structures: readonly StructureState[]): readonly StructureState[] {
  if (lastWalls && lastWalls.from === structures) return lastWalls.walls;
  const walls = structures.filter(blocksSight), prev = lastWalls?.walls;
  const same = !!prev && prev.length === walls.length && prev.every((w, i) => w === walls[i]);
  lastWalls = { from: structures, walls: same ? prev! : walls };
  return lastWalls.walls;
}
/** 69.15: the planet a world point stands on (the nearest surface; −1 with none). */
function planetAt(planets: GameState['planets'], pt: Vec3): number {
  let best = -1, bestD = Infinity;
  planets.forEach((p, i) => { const d = Math.abs(vecLength(vecSub(pt, p.position)) - p.radius); if (d < bestD) { bestD = d; best = i; } });
  return best;
}
/**
 * 69.15: does a wall (blocksSight) stand across the sight line a→b? The planet's standing shapes that are walls (the fire
 * line's shapes, segmentBlockedStanding's), each met as the fire line meets it — a shape holding either end left out (the eye of a gunner seated in a
 * wreck's shadow, a radar's head on its mount: the body it looks out of is no wall to it).
 */
export function wallsAcross(a: Vec3, b: Vec3, state: Pick<GameState, 'rocks' | 'flora' | 'structures' | 'planets'> & Partial<Pick<GameState, 'config' | 'ruins'>>): boolean {
  const walls = sightWalls(state.structures);
  if (!walls.length && !(state.ruins?.length)) return false;
  const planetIndex = planetAt(state.planets, a);
  if (!state.planets[planetIndex]) return false;
  const shapes = planetShapes(state, planetIndex);
  // (the walls are a few dozen at most: their shapes tested in turn — no query along the line, whose work the frame budget counts)
  let w = wallShapes.get(shapes);
  if (!w || w.walls !== walls) {
    const ids = new Set(walls.map(x => x.id)), idx: number[] = [];
    shapes.list.forEach((sh, i) => { if ((sh.ref.kind === 'structure' && ids.has(sh.ref.id)) || sh.ref.kind === 'ruin') idx.push(i); });
    wallShapes.set(shapes, w = { walls, idx });
  }
  for (const i of w.idx) {
    const sh = shapes.list[i], meet = mayMeet(a, b, sh);
    if (meet === 0) continue;
    const crosses = sh.sub ? subPrims(sh, a, b).some(k => partEntry(a, b, sh.prims[k], meet) !== null) : sh.prims.some(p => partEntry(a, b, p, meet) !== null);
    // (a shape holding an end is the body the eye looks out of: asked only of a shape the line crosses)
    if (crosses && !sh.prims.some(p => primDistance(a, p) <= 0 || primDistance(b, p) <= 0)) return true;
  }
  return false;
}

/**
 * How far `pt` is from a prim (0 inside). A frustum's is measured across, from its axis less its radius at that height
 * (its slope ignored: for a tapered one it errs long by under the taper's share); a box's exactly.
 */
export function primDistance(pt: Vec3, p: Prim): number {
  hitWork.prims++;
  if (p.cyl) {
    const s = vecSub(pt, p.base), al = vecDot(s, p.up), across = horizontal(s, p.up);
    const h = Math.max(p.h0, Math.min(p.h1, al));
    const g = p.h1 > p.h0 ? (p.r1 - p.r0) / (p.h1 - p.h0) : 0;
    const out = Math.max(0, across - (p.r0 + g * (h - p.h0))), along = al - h;
    return Math.sqrt(out * out + along * along);
  }
  const s = vecSub(pt, p.c);
  const x = Math.max(0, Math.abs(vecDot(s, p.ax)) - p.hx), y = Math.max(0, Math.abs(vecDot(s, p.ay)) - p.hy), z = Math.max(0, Math.abs(vecDot(s, p.az)) - p.hz);
  return Math.sqrt(x * x + y * y + z * z);
}

/** The point of a prim nearest `pt` (`pt` itself inside); a frustum's measured across from its axis, as primDistance's. */
export function primNearest(pt: Vec3, p: Prim): Vec3 {
  hitWork.prims++;
  if (p.cyl) {
    const s = vecSub(pt, p.base), al = vecDot(s, p.up);
    const h = Math.max(p.h0, Math.min(p.h1, al));
    const g = p.h1 > p.h0 ? (p.r1 - p.r0) / (p.h1 - p.h0) : 0;
    const r = p.r0 + g * (h - p.h0);
    const radial = vecSub(s, vecScale(p.up, al)), across = vecLength(radial);
    return vecAdd(vecAdd(p.base, vecScale(p.up, h)), across > r ? vecScale(radial, r / across) : radial);
  }
  const s = vecSub(pt, p.c);
  const x = Math.max(-p.hx, Math.min(p.hx, vecDot(s, p.ax))), y = Math.max(-p.hy, Math.min(p.hy, vecDot(s, p.ay))), z = Math.max(-p.hz, Math.min(p.hz, vecDot(s, p.az)));
  return vecAdd(p.c, vecAdd(vecScale(p.ax, x), vecAdd(vecScale(p.ay, y), vecScale(p.az, z))));
}

/**
 * 63.4 step 7: the point of a structure's shape nearest `pt` (over all its parts) and how far it is — null when the
 * structure has no shape on its planet (lower than a step: nothing a bullet or a walker meets).
 */
export function structureNearest(
  pt: Vec3, s: Pick<StructureState, 'id' | 'planetIndex'>, state: Pick<GameState, 'rocks' | 'flora' | 'structures' | 'planets'> & Partial<Pick<GameState, 'config'>>,
): { dist: number; at: Vec3 } | null {
  if (!state.planets[s.planetIndex]) return null;
  const shapes = planetShapes(state, s.planetIndex);
  if (!shapes.byStructure) {
    const by = new Map<number, number>();
    shapes.list.forEach((sh, i) => { if (sh.ref.kind === 'structure') by.set(sh.ref.id, i); });
    shapes.byStructure = by;
  }
  const i = shapes.byStructure.get(s.id);
  // 63.1d step 2: a body that drives is shaped where it stands this tick, apart from the filed shapes
  const prims = i !== undefined ? shapes.list[i].prims : movingShapes(state, s.planetIndex).find(sh => sh.ref.id === s.id)?.prims;
  if (!prims) return null;
  let best: { dist: number; at: Vec3 } | null = null;
  for (const p of prims) {
    const at = primNearest(pt, p), dist = vecLength(vecSub(pt, at));
    if (!best || dist < best.dist) best = { dist, at };
  }
  return best;
}

// ---------------------------------------------------------------------------
// 63.1d step 2: the bodies that drive, shaped where they stand this tick
// ---------------------------------------------------------------------------

// (69.72 step 1 (a): each entry keeps where its driving records stood and which they were — an array written into in place
// after the memo was taken (stepDrive's slice, a vehicle moved after another's moveBike) is seen, not served stale)
const movingMemo = new WeakMap<readonly StructureState[], Map<number, { planet: PlanetState; config: unknown; list: HitShape[]; where: number[]; recs: StructureState[] }>>();
const NO_MOVING: HitShape[] = [];

/**
 * The hulls of the bodies that drive on planet `planetIndex` (a bike, a transport), each its measured shape
 * (constants/hitboxes.ts, in people) in its seat on the ground (core/vehicleFrame.ts — the frame the host draws it in),
 * with a bounding cylinder so a miss costs one test. They move every tick, so they are kept apart from the planet's filed
 * shapes and rebuilt when the structures change (a handful of bodies; memoised on the structures array).
 */
export function movingShapes(state: Pick<GameState, 'structures' | 'planets'> & Partial<Pick<GameState, 'config'>>, planetIndex: number): readonly HitShape[] {
  const structures = state.structures ?? [];
  if (!structures.some(drives)) return NO_MOVING;
  const planet = state.planets[planetIndex];
  if (!planet) return NO_MOVING;
  let per = movingMemo.get(structures);
  if (!per) { per = new Map(); movingMemo.set(structures, per); }
  const hit = per.get(planetIndex);
  if (hit && hit.planet === planet && hit.config === state.config && hit.recs.every((r, k) => structures[hit.where[k]] === r)) return hit.list;
  const ps = state.config ? personSetOf({ config: state.config }) : null;
  const person = PERSON_BODY_HEIGHT * (ps ? ps.k : 1);
  const ground = { fineAmplitude: ps?.fineAmplitude ?? 0, fineWavelength: ps?.fineWavelength ?? 1, k: ps?.k ?? 1 };   // (71 step 0: k too — no relief without a person set)
  const list: HitShape[] = [], where: number[] = [], recs: StructureState[] = [];
  for (const [i, s] of structures.entries()) {
    if (!drives(s) || s.planetIndex !== planetIndex) continue;
    where.push(i); recs.push(s);
    list.push(keptShape(planet, s, person, ground));
  }
  per.set(planetIndex, { planet, config: state.config, list, where, recs });
  return list;
}

/** 63.19: one driving body's hull where it stands now (movingShapes' entry for it, without rebuilding the planet's list after each hull's move). */
export function drivenHullShape(state: Pick<GameState, 'planets'> & Partial<Pick<GameState, 'config'>>, s: StructureState): HitShape {
  const ps = state.config ? personSetOf({ config: state.config }) : null;
  const ground = { fineAmplitude: ps?.fineAmplitude ?? 0, fineWavelength: ps?.fineWavelength ?? 1, k: ps?.k ?? 1 };
  return keptShape(state.planets[s.planetIndex], s, PERSON_BODY_HEIGHT * (ps ? ps.k : 1), ground);
}

/** One driving body's hull where it stands: its parts placed in its frame (core/vehicleFrame.ts), the turret's turned. */
function movingShapeOf(planet: PlanetState, s: StructureState, person: number, ground: GroundDials): HitShape {
  const def = BODIES[s.type].hitbox;
  const scale = def.ruler === 'unit' ? PERSON_BODY_HEIGHT : person;
  const ruin = s.health > 0 ? 1 : (BODIES[s.type].ruinHeight ?? 1);
  const f = vehicleFrame(planet, s, person, ground);
  const at = (x: number, y: number, z: number) => vecAdd(f.foot, vecAdd(vecScale(f.left, x * scale), vecAdd(vecScale(f.up, y * scale * ruin), vecScale(f.front, z * scale))));
  // 69.29: a turret's parts (a tank's) stand turned by its yaw about the hull's up, through the turret's axis — as it is drawn
  // (the drawn turret group's rotation.y about its own origin: its front toward the hull's left by the yaw)
  const from = TURRET_PARTS_FROM[s.type as BodyKey], yaw = from !== undefined && s.turretYaw && s.health > 0 ? s.turretYaw : 0;
  const c = yaw ? dcos(yaw) : 1, sn = yaw ? dsin(yaw) : 0;
  const tLeft = yaw ? vecSub(vecScale(f.left, c), vecScale(f.front, sn)) : f.left, tFront = yaw ? vecAdd(vecScale(f.front, c), vecScale(f.left, sn)) : f.front;
  const z0 = s.type === 'tank' ? TANK.turretPivotZ : 0, axis = vecAdd(f.foot, vecScale(f.front, z0 * scale));
  const atT = (x: number, y: number, z: number) => vecAdd(axis, vecAdd(vecScale(tLeft, x * scale), vecAdd(vecScale(f.up, y * scale * ruin), vecScale(tFront, (z - z0) * scale))));
  const prims: Prim[] = [];
  let reach = 0, top = 0;
  for (const [k, part] of def.parts.entries()) {
    const turned = yaw !== 0 && k >= from!;
    const p = turned ? partPrim(part, atT, tLeft, f.up, tFront, scale, ruin) : partPrim(part, at, f.left, f.up, f.front, scale, ruin);
    prims.push(p);
    if (p.cyl) { reach = Math.max(reach, horizontal(vecSub(p.base, f.foot), f.up) + Math.max(p.r0, p.r1)); top = Math.max(top, p.h1); }
    else { reach = Math.max(reach, horizontal(vecSub(p.c, f.foot), f.up) + Math.sqrt(p.hx * p.hx + p.hz * p.hz)); top = Math.max(top, vecDot(vecSub(p.c, f.foot), f.up) + p.hy); }
  }
  return { ref: { kind: 'structure', id: s.id }, dir: f.up, prims, reach, bound: { cyl: true, base: f.foot, up: f.up, r0: reach, r1: reach, h0: 0, h1: top } };
}

/** 69.72 step 1 (b) (ARCHITECTURE §5.33 item 1): each body's hull kept while nothing it is built from has changed — the planet
 * (a ground edit makes a new one), its type, spot, heading, turret's yaw, alive or a ruin, and the person's size and fine
 * ground. A parked vehicle's hull is built once, not every tick (72–90 % of the fight board's are parked). The same
 * numbers as a build: a kept hull is the very object its build made (Object.is on every input, so -0 is not 0). */
const keptHulls = new WeakMap<PlanetState, Map<number, { s: StructureState; person: number; amp: number; wave: number; shape: HitShape }>>();
function sameInputs(a: StructureState, b: StructureState): boolean {
  if (a === b) return true;
  const ha = a.drive?.heading, hb = b.drive?.heading;
  return a.type === b.type && Object.is(a.surfaceDir.x, b.surfaceDir.x) && Object.is(a.surfaceDir.y, b.surfaceDir.y) && Object.is(a.surfaceDir.z, b.surfaceDir.z)
    && (ha === hb || (!!ha && !!hb && Object.is(ha.x, hb.x) && Object.is(ha.y, hb.y) && Object.is(ha.z, hb.z)))
    && Object.is(a.turretYaw, b.turretYaw) && (a.health > 0) === (b.health > 0);
}
function keptShape(planet: PlanetState, s: StructureState, person: number, ground: GroundDials): HitShape {
  let byId = keptHulls.get(planet);
  if (!byId) { byId = new Map(); keptHulls.set(planet, byId); }
  const k = byId.get(s.id);
  if (k && Object.is(k.person, person) && Object.is(k.amp, ground.fineAmplitude) && Object.is(k.wave, ground.fineWavelength) && sameInputs(k.s, s)) return k.shape;
  const shape = movingShapeOf(planet, s, person, ground);
  byId.set(s.id, { s, person, amp: ground.fineAmplitude, wave: ground.fineWavelength, shape });
  return shape;
}

/** 63.1d step 2: where a→b first enters a moving hull on planet `planetIndex` (0…1), and whose, or null. */
export function firstMovingHit(a: Vec3, b: Vec3, planetIndex: number, state: Pick<GameState, 'structures' | 'planets'> & Partial<Pick<GameState, 'config'>>, exceptId?: number): { t: number; ref: ObstacleRef } | null {
  let best: { t: number; ref: ObstacleRef } | null = null;
  for (const sh of movingShapes(state, planetIndex)) {
    if (sh.ref.id === exceptId) continue;   // 63.1d step 3: a round from a vehicle's own gun leaves its hull
    const meet = mayMeet(a, b, sh);
    if (meet === 0) continue;
    for (const p of sh.prims) {
      const t = partEntry(a, b, p, meet);
      if (t !== null && (!best || t < best.t)) best = { t, ref: sh.ref };
    }
  }
  return best;
}

/** 63.1d step 2: a person's hip over the person's ground at `dir` (walking's test point against a hull). */
/** 63.1d's follow-up: how far `pt` is from the nearest vehicle hull's part within `within` (Infinity: none that near). */
export function hullClearance(pt: Vec3, within: number, planetIndex: number, state: Pick<GameState, 'structures' | 'planets'> & Partial<Pick<GameState, 'config'>>): number {
  let best = Infinity;
  for (const sh of movingShapes(state, planetIndex)) {
    if (sh.bound && primDistance(pt, sh.bound) > within) continue;
    for (const p of sh.prims) best = Math.min(best, primDistance(pt, p));
  }
  return best;
}
export function personHip(planet: PlanetState, dir: Vec3, ps: Pick<PersonSet, 'fineAmplitude' | 'fineWavelength' | 'k'>): Vec3 {
  const d = vecNormalize(dir);
  return vecAdd(planet.position, vecScale(d, personSurfaceRadius(planet, d, ps) + 0.5 * PERSON_BODY_HEIGHT * ps.k));
}

/**
 * 63.1d's follow-up (the owner: "when I exit the transport I get stuck in the door on the side and can't move"): a person on
 * foot whose hip is inside a vehicle's hull (where the hull stands this tick) — a hull driven onto it, or set down in it —
 * is moved out of it before it walks, or every step would be refused. Out along the nearest free
 * way: from the deepest part it overlaps, away from that part's nearest point (from inside a box, through its nearest
 * side, across the ground), to its radius and a twentieth of a person clear. Null when it is clear already.
 */
export function outOfHulls(state: Pick<GameState, 'structures' | 'planets'> & Partial<Pick<GameState, 'config'>>, planetIndex: number, dir: Vec3, ps: Pick<PersonSet, 'fineAmplitude' | 'fineWavelength' | 'k' | 'unitRadius'>): Vec3 | null {
  const planet = state.planets[planetIndex];
  if (!planet) return null;
  const r = ps.unitRadius, up = vecNormalize(dir), hip = personHip(planet, up, ps);
  let best: { away: Vec3; need: number } | null = null;
  // only from inside a hull (driven onto, or set down in it): a person merely within its radius walks out by itself (the
  // walk lets it step away — match.ts)
  if (!movingShapes(state, planetIndex).some(sh => sh.prims.some(p => primDistance(hip, p) <= 0))) return null;
  for (const sh of movingShapes(state, planetIndex)) {
    if (sh.bound && primDistance(hip, sh.bound) > r) continue;
    for (const p of sh.prims) {
      const d = primDistance(hip, p);
      if (d > r) continue;
      let away: Vec3, need: number;
      if (d > 1e-9) { away = vecSub(hip, primNearest(hip, p)); need = r - d; }
      else if (!p.cyl) {
        const sx = hip.x - p.c.x, sy = hip.y - p.c.y, sz = hip.z - p.c.z;
        const lx = sx * p.ax.x + sy * p.ax.y + sz * p.ax.z, lz = sx * p.az.x + sy * p.az.y + sz * p.az.z;
        const ox = p.hx - Math.abs(lx), oz = p.hz - Math.abs(lz);
        away = ox <= oz ? vecScale(p.ax, lx >= 0 ? 1 : -1) : vecScale(p.az, lz >= 0 ? 1 : -1);
        need = Math.min(ox, oz) + r;
      } else {
        const s2 = vecSub(hip, p.base), radial = vecSub(s2, vecScale(p.up, vecDot(s2, p.up)));
        away = vecLength(radial) > 1e-12 ? radial : sh.dir;
        need = Math.max(p.r0, p.r1) - vecLength(radial) + r;
      }
      const t = vecSub(away, vecScale(up, vecDot(away, up)));
      if (vecLength(t) < 1e-12) continue;
      if (!best || need > best.need) best = { away: vecNormalize(t), need };
    }
  }
  if (!best) return null;
  const th = (best.need + 0.05 * PERSON_BODY_HEIGHT * ps.k) / planet.radius;
  return vecNormalize(vecAdd(vecScale(up, dcos(th)), vecScale(best.away, dsin(th))));
}

/** 63.1d step 2: is `pt` within `radius` of a moving hull on planet `planetIndex` (but `exceptId`'s, the mover's own)? Walking's test against a vehicle. */
export function movingHullNear(pt: Vec3, radius: number, planetIndex: number, state: Pick<GameState, 'structures' | 'planets'> & Partial<Pick<GameState, 'config'>>, exceptId?: number): boolean {
  for (const sh of movingShapes(state, planetIndex)) {
    if (sh.ref.id === exceptId) continue;
    if (sh.bound && primDistance(pt, sh.bound) > radius) continue;
    for (const p of sh.prims) if (primDistance(pt, p) <= radius) return true;
  }
  return false;
}

/**
 * 63.4 step 6: the props (trees and rocks) whose shape comes within `radius` of `pt` on planet `planetIndex`, with how
 * far, in list order — a blast's reach on props, measured to the trunk or the rock rather than its base.
 */
export function propsWithin(
  pt: Vec3, radius: number, planetIndex: number, state: Pick<GameState, 'rocks' | 'flora' | 'structures' | 'planets'> & Partial<Pick<GameState, 'config'>>,
): Array<{ ref: ObstacleRef; dist: number }> {
  const planet = state.planets[planetIndex];
  if (!planet) return [];
  const shapes = planetShapes(state, planetIndex);
  const all = shapes.list.length;
  const found: number[] = [];
  const floor = vecLength(vecSub(pt, planet.position)) - shapes.maxTop - radius;
  if (all < 32 || !(floor > 0)) for (let i = 0; i < all; i++) found.push(i);
  else {
    hitWork.queries++;
    { const c = queryCellIndexCount(shapes.index, vecNormalize(vecSub(pt, planet.position)), (1.1 * (radius + shapes.maxReach)) / floor + 1e-9, false), ids = cellQueryIds(); for (let q = 0; q < c; q++) found.push(shapes.filed[ids[q]]); }
    for (const L of shapes.large) if (vecLength(vecSub(pt, L.c)) <= L.r + radius) found.push(L.i);
    found.sort((a, b) => a - b);
  }
  hitWork.candidates += found.length;
  const out: Array<{ ref: ObstacleRef; dist: number }> = [];
  for (const i of found) {
    const sh = shapes.list[i];
    if (sh.ref.kind === 'structure' || sh.ref.kind === 'ruin' || sh.ref.kind === 'debris') continue;   // bodies, not props: a ruin takes no blow, nor what fell
    if (sh.bound && primDistance(pt, sh.bound) > radius) continue;   // E5.2: the bound is nearer than any part
    let d = Infinity;
    for (const p of sh.prims) d = Math.min(d, primDistance(pt, p));
    if (d <= radius) out.push({ ref: sh.ref, dist: d });
  }
  return out;
}

/**
 * 66.7 F1 (core/coverMap.ts): the shapes that could reach within `radius` of `pt` — a superset, as indices into
 * `shapes.list` in its order (ascending), so an exact test over them answers as one over the whole list. `propsWithin`'s
 * query, without its props-only filter.
 */
export function shapesWithin(pt: Vec3, radius: number, planet: PlanetState, shapes: PlanetShapes): number[] {
  const all = shapes.list.length, found: number[] = [];
  const floor = vecLength(vecSub(pt, planet.position)) - shapes.maxTop - radius;
  if (all < 32 || !(floor > 0)) { for (let i = 0; i < all; i++) found.push(i); hitWork.candidates += all; return found; }
  hitWork.queries++;
  { const c = queryCellIndexCount(shapes.index, vecNormalize(vecSub(pt, planet.position)), (1.1 * (radius + shapes.maxReach)) / floor + 1e-9, false), ids = cellQueryIds(); for (let q = 0; q < c; q++) found.push(shapes.filed[ids[q]]); }
  for (const L of shapes.large) if (vecLength(vecSub(pt, L.c)) <= L.r + radius) found.push(L.i);
  found.sort((a, b) => a - b);
  hitWork.candidates += found.length;
  return found;
}

/**
 * Is `pt` inside any shape on planet `planetIndex`? (The crosshair's samples.) `skip`: a prop looked past — the cover the
 * player holds (66.7 (a): the crosshair converges past the rock you are pressed to, as a seated gun's past its own hull).
 */
export function pointBlocked(pt: Vec3, planetIndex: number, state: Pick<GameState, 'rocks' | 'flora' | 'structures' | 'planets'> & Partial<Pick<GameState, 'config'>>, skip?: ObstacleRef): boolean {
  const planet = state.planets[planetIndex];
  const shapes = planetShapes(state, planetIndex);
  const moving = (): boolean => movingShapes(state, planetIndex).some(sh => sh.prims.some(p => pointInPrim(pt, p)));   // 63.1d step 2
  if (shapes.list.length === 0) return moving();
  for (const i of shapesNear([pt], planet, shapes, nearScratch)) {
    const sh = shapes.list[i];
    if (skip && sh.ref.kind === skip.kind && sh.ref.id === skip.id) continue;
    if (sh.bound && !pointInPrim(pt, sh.bound)) continue;
    if (sh.sub) { for (const k of subPrimsAt(sh, pt)) if (pointInPrim(pt, sh.prims[k])) return true; continue; }   // 63.14 step 4
    for (const p of sh.prims) if (pointInPrim(pt, p)) return true;
  }
  return moving();
}
